import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useUWBTracking } from '../hooks/useUWBTracking';
import { motion } from 'framer-motion';
import GoogleMapView from './GoogleMapView';
import WarehouseConflictDialog from './WarehouseConflictDialog';
import BlueprintEditor from './BlueprintEditor';
import UWBTagManager from './UWBTagManager';
import UWBCalibration from './UWBCalibration';
import FeatureGate from './FeatureGate';
import api from '../lib/api';

const spring = { type: 'spring', stiffness: 60, damping: 20 };


export default function WarehouseMap({ data, selectedAssetId, selectedZoneId, selectedRackId, selectedAssetType, onZoneSelect, onAssetSelect, onRackSelect, onViewChange, currentWarehouseId, warehouses = {}, onWarehouseChange, onCreateWarehouse, onZoneUpdate, onBlueprintUpdate, checkWarehouseExists, onUpdateWarehouseGPS, organizations = [], onWarehouseClick = null, warehouseNavTarget = null, onAssetPositionUpdate = null, hubs = [], onHubCreate = null, onHubPositionUpdate = null, onHubDelete = null, onHubNameUpdate = null, trackedBeacons = [], onClearTestBeacons = null, traceMode = null, onTraceTimeChange = null, selectedForkliftInfo = null, onForkliftInfoChange = null }) {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const { isAdmin } = usePermissions();
  const { uwbTags, ghostTags, connected: uwbConnected, getTagTrace, positioningMethod, setPositioningMethod } = useUWBTracking(currentWarehouseId, hubs);
  
  // Debug: Log UWB tags for troubleshooting
  useEffect(() => {
    if (uwbTags && uwbTags.length > 0) {
      console.log(`[WarehouseMap] UWB Tags loaded: ${uwbTags.length} tags`, uwbTags.map(t => ({
        name: t.tag_name,
        id: t.tag_id,
        hardware_id: t.tag_hardware_id,
        hasPosition: !!(t.position && typeof t.position.x === 'number'),
        position: t.position,
        warehouse_id: t.warehouse_id,
        status: t.status,
        hub_count: t.hub_count
      })));
      
      // Count tags with/without positions
      const withPosition = uwbTags.filter(t => t.position && typeof t.position.x === 'number').length;
      const withoutPosition = uwbTags.length - withPosition;
      if (withoutPosition > 0) {
        console.warn(`[WarehouseMap] ${withoutPosition} UWB tag(s) without position data out of ${uwbTags.length} total`);
      }
    } else if (currentWarehouseId) {
      console.log(`[WarehouseMap] No UWB tags loaded for warehouse ${currentWarehouseId}`);
    }
  }, [uwbTags, currentWarehouseId]);
  const [activeView, setActiveView] = useState({ type: 'default', id: null });
  const [hoveredZoneId, setHoveredZoneId] = useState(null);
  const [hoveredHubId, setHoveredHubId] = useState(null);
  const [useGoogleView, setUseGoogleView] = useState(false);
  const [mapType, setMapType] = useState('roadmap');
  const [buildMode, setBuildMode] = useState(false);
  const [buildPoints, setBuildPoints] = useState([]);
  const [buildPointsHistory, setBuildPointsHistory] = useState([]); // History for undo/redo
  const [buildPointsHistoryIndex, setBuildPointsHistoryIndex] = useState(-1);
  const [selectedPointIndex, setSelectedPointIndex] = useState(null); // Selected point for deletion
  const [gpsBuildSubMode, setGpsBuildSubMode] = useState('create'); // 'create' | 'edit'
  const [editingWarehouseId, setEditingWarehouseId] = useState(null); // ID of warehouse being edited in GPS edit mode
  const [selectedOrgId, setSelectedOrgId] = useState(null); // Organization to link new warehouse to
  const [editMode, setEditMode] = useState(false);
  const [selectedZoneForEdit, setSelectedZoneForEdit] = useState(null); // Selected zone ID for deletion
  const [blueprintEditMode, setBlueprintEditMode] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [draggingZonePosition, setDraggingZonePosition] = useState(null); // { id, x, y, width, height } for smooth dragging
  const [conflictDialog, setConflictDialog] = useState(null); // { name, existingWarehouse, layoutData }
  const [showBlueprintEditor, setShowBlueprintEditor] = useState(false);
  const [blueprintHistory, setBlueprintHistory] = useState([]); // History stack for undo
  const [blueprintHistoryIndex, setBlueprintHistoryIndex] = useState(-1);
  const [zoneHistory, setZoneHistory] = useState([]); // History stack for zones
  const [zoneHistoryIndex, setZoneHistoryIndex] = useState(-1);
  const [addCornerMode, setAddCornerMode] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y, type, index }
  const [buildModeDropdownOpen, setBuildModeDropdownOpen] = useState(false); // Build Mode dropdown state
  const [calibrateModeDropdownOpen, setCalibrateModeDropdownOpen] = useState(false); // Calibrate Mode dropdown state
  const [showUWBCalibration, setShowUWBCalibration] = useState(false); // UWB Auto-Calibration modal
  
  // Asset and Hub dragging state
  const [draggingAsset, setDraggingAsset] = useState(null); // { id, startX, startY, currentX, currentY }
  const [draggingHub, setDraggingHub] = useState(null); // { id, startX, startY, currentX, currentY }
  const [hubPlacementMode, setHubPlacementMode] = useState(false); // Mode for adding new hubs
  const [pendingHubPosition, setPendingHubPosition] = useState(null); // Position where user clicked to place hub
  const [showHubModal, setShowHubModal] = useState(false); // Show hub creation modal
  const [showTagManager, setShowTagManager] = useState(false); // Show UWB tag manager
  const [selectedHubId, setSelectedHubId] = useState(null); // Currently selected hub
  const [editingHubId, setEditingHubId] = useState(null); // Hub being renamed
  const [editingHubName, setEditingHubName] = useState(''); // New name for hub being renamed
  const [showZones, setShowZones] = useState(true); // Toggle zones visibility
  const [showAssets, setShowAssets] = useState(true); // Toggle assets visibility
  const [showHubs, setShowHubs] = useState(true); // Toggle hubs visibility
  const [showUWBDistances, setShowUWBDistances] = useState(false); // Toggle UWB distance readings (lines)
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false); // Filter dropdown state
  const filterDropdownRef = useRef(null); // Ref for filter dropdown
  const svgRef = useRef(null);
  const [activeHubTagReadings, setActiveHubTagReadings] = useState(new Map()); // Map of "hubId-tagId" -> { lastReadingAt, latestDistance }
  const [forkliftCarryingStatus, setForkliftCarryingStatus] = useState(new Map()); // Map of tag_id -> { carrying: bool, asset: {...} }
  
  // Pan and zoom state — kept SEPARATE from activeView so it never affects
  // the Reset button visibility or Build/Calibrate mode availability.
  const [panZoomViewBox, setPanZoomViewBox] = useState(null); // null = no override
  const mapContainerRef = useRef(null);
  const viewBoxRef = useRef(null);          // always holds the latest rendered viewBox
  const isPanningRef = useRef(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const editModeRef = useRef(false);
  const blueprintEditModeRef = useRef(false);
  const buildModeRef = useRef(false);
  const hubPlacementModeRef = useRef(false);
  const addCornerModeRef = useRef(false);
  const dragStateRef = useRef(null);

  // Debug: Log hubs when they change (only log once, not on every render)
  const hubsLoggedRef = useRef(new Set());
  useEffect(() => {
    // Only log if hubs actually changed (by checking hub IDs)
    const hubIds = hubs?.map(h => h.id).join(',') || '';
    if (!hubsLoggedRef.current.has(hubIds)) {
      hubsLoggedRef.current.add(hubIds);
      // Clear old entries to prevent memory leak (keep last 10)
      if (hubsLoggedRef.current.size > 10) {
        const first = hubsLoggedRef.current.values().next().value;
        hubsLoggedRef.current.delete(first);
      }
      
      if (hubs && hubs.length > 0) {
        const filtered = hubs.filter(h => h.warehouse_id === currentWarehouseId || (!h.warehouse_id && currentWarehouseId));
        console.log(`[WarehouseMap] ${hubs.length} hubs loaded, ${filtered.length} match warehouse ${currentWarehouseId}`);
      } else {
        console.log(`[WarehouseMap] No hubs loaded`);
      }
    }
  }, [hubs, currentWarehouseId]);

  // G2 Supply (Main Warehouse) center - default view for Google Maps
  const externalWarehouseCenter = useMemo(() => ({
    lat: 34.55514594090189,
    lng: -85.61576981355337,
  }), []);

  const zonesById = useMemo(() => Object.fromEntries(data.zones.map(z => [z.id, z])), [data.zones]);
  const assetsById = useMemo(() => Object.fromEntries(data.assets.map(a => [a.id, a])), [data.assets]);
  
  
  // Filter beacons for current warehouse
  const filteredBeacons = useMemo(() => {
    if (!trackedBeacons || !currentWarehouseId || useGoogleView) return [];
    return trackedBeacons.filter(b => {
      // If warehouseId is missing, show it anyway (fallback for backwards compatibility)
      if (!b.warehouseId && currentWarehouseId) {
        return true; // Show beacons without warehouseId if we're in a warehouse view
      }
      return b.warehouseId === currentWarehouseId;
    });
  }, [trackedBeacons, currentWarehouseId, useGoogleView]);
  
  // Debug: Log filtered beacons
  useEffect(() => {
    if (filteredBeacons.length > 0 && trackedBeacons.length !== filteredBeacons.length) {
      console.log(`[WarehouseMap] Filtered ${trackedBeacons.length} beacons to ${filteredBeacons.length} for warehouse ${currentWarehouseId}`);
    }
    if (filteredBeacons.length > 0) {
      console.log(`[WarehouseMap] Rendering ${filteredBeacons.length} beacons:`, filteredBeacons.map(b => ({
        id: b.bleTagId,
        pos: b.position,
        warehouseId: b.warehouseId
      })));
    }
  }, [filteredBeacons.length, trackedBeacons.length, currentWarehouseId]);

  // Poll for recent UWB distance readings (to determine active hub-tag pairs)
  useEffect(() => {
    if (!showUWBDistances || !currentWarehouseId) return;

    const fetchRecentReadings = async () => {
      try {
        const result = await api.get(`/api/uwb/readings/recent?warehouseId=${currentWarehouseId}`);
        if (result.success && result.readings) {
          // Create a Map of "hubId-tagId" -> reading data
          const newReadingsMap = new Map();
          const now = new Date();
          
          result.readings.forEach(reading => {
            const key = `${reading.hubId}-${reading.tagId}`;
            const lastReadingAt = new Date(reading.lastReadingAt);
            const secondsSinceReading = (now - lastReadingAt) / 1000;
            
            // Only include readings from last 15 seconds
            if (secondsSinceReading <= 15) {
              newReadingsMap.set(key, {
                hubId: reading.hubId,
                tagId: reading.tagId,
                tagHardwareId: reading.tagHardwareId,
                lastReadingAt: reading.lastReadingAt,
                latestDistance: reading.latestDistance
              });
            }
          });
          
          setActiveHubTagReadings(newReadingsMap);
        }
      } catch (error) {
        console.error('Error fetching recent UWB readings:', error);
      }
    };

    // Initial fetch
    fetchRecentReadings();

    // Poll every 2 seconds
    const interval = setInterval(fetchRecentReadings, 2000);

    return () => clearInterval(interval);
  }, [showUWBDistances, currentWarehouseId]);

  // Poll for forklift carrying status
  useEffect(() => {
    if (!uwbTags || uwbTags.length === 0) return;

    const fetchCarryingStatus = async () => {
      try {
        const statusMap = new Map();
        
        // Check each forklift tag
        for (const tag of uwbTags) {
          // Use tag_hardware_id (e.g., "FORKLIFT_001") not tag_id (database UUID)
          const hardwareId = tag.tag_hardware_id || tag.tag_id;
          if (!hardwareId) continue;
          
          try {
            const result = await api.get(`/api/vision/current-asset/${hardwareId}`);
            if (result.success) {
              statusMap.set(tag.tag_id, {
                carrying: result.carrying,
                asset: result.asset
              });
            }
          } catch (error) {
            // Tag might not have any detections, that's okay
            statusMap.set(tag.tag_id, { carrying: false, asset: null });
          }
        }
        
        setForkliftCarryingStatus(statusMap);
      } catch (error) {
        console.error('Error fetching carrying status:', error);
      }
    };

    // Initial fetch
    fetchCarryingStatus();

    // Poll every 3 seconds
    const interval = setInterval(fetchCarryingStatus, 3000);

    return () => clearInterval(interval);
  }, [uwbTags]);

  // Close filter dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target)) {
        setFilterDropdownOpen(false);
      }
    };

    if (filterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [filterDropdownOpen]);
  
  // Filter out zones that are actually warehouses (have GPS polygons)
  // These should only appear in Google Map view, not in the warehouse SVG view
  const mainViewZones = useMemo(() => {
    return data.zones.filter(z => {
      // Exclude zones with GPS polygons (these are warehouses, not zones)
      if (z.gps && z.gps.polygon) return false;
      // Also filter by known warehouse-like IDs (for backward compatibility)
      const name = (z.name || '').toLowerCase();
      const isWarehouseLike = name.includes('gametime') ||
                             name.includes('childrens place') ||
                             name.includes('children') ||
                             name.includes('gh metals') ||
                             name.includes('gh-metals');
      return !isWarehouseLike;
    });
  }, [data.zones]);
  // External assets to show on Google Map view
  // Mock assets removed - user will add assets later
  const externalAssets = useMemo(() => {
    return [];
  }, []);


  // Get assets in the selected rack
  const selectedRackAssets = useMemo(() => {
    if (!selectedRackId) return [];
    return data.assets.filter(asset => asset.rowId === selectedRackId);
  }, [data.assets, selectedRackId]);

  // Find which zone contains the selected rack
  const selectedRackZoneId = useMemo(() => {
    if (!selectedRackId || selectedRackAssets.length === 0) return null;
    return selectedRackAssets[0].zoneId;
  }, [selectedRackId, selectedRackAssets]);

  // Calculate which zone contains the selected asset
  const selectedAssetZoneId = useMemo(() => {
    if (!selectedAssetId) return null;
    const asset = assetsById[selectedAssetId];
    return asset ? asset.zoneId : null;
  }, [selectedAssetId, assetsById]);

  // Calculate opacity for each zone based on asset selection
  const getZoneOpacity = useCallback((zoneId) => {
    if (!selectedAssetId && !selectedAssetType) return 1;
    if (selectedAssetType) {
      // If filtering by type, dim zones that do not contain any assets of this type
      const hasType = data.assets.some(a => a.zoneId === zoneId && a.assetType === selectedAssetType);
      return hasType ? 1 : 0.15;
    }
    return zoneId === selectedAssetZoneId ? 1 : 0.3;
  }, [selectedAssetId, selectedAssetZoneId, selectedAssetType, data.assets]);

  // Calculate current trace position during trace mode
  const getTracePosition = useMemo(() => {
    if (!traceMode?.isActive || !traceMode.trace || traceMode.trace.length === 0) {
      return null;
    }
    
    const { trace, duration } = traceMode;
    const currentTime = traceMode.currentTime;
    
    // Find the two points to interpolate between
    const normalizedTime = currentTime / duration;
    const totalTime = duration;
    const targetTime = normalizedTime * totalTime;
    
    // Find the segment we're in
    let segmentIndex = 0;
    for (let i = 0; i < trace.length - 1; i++) {
      const pointTime = (new Date(trace[i].created_at) - new Date(trace[0].created_at)) / 1000;
      const nextPointTime = (new Date(trace[i + 1].created_at) - new Date(trace[0].created_at)) / 1000;
      
      if (targetTime >= pointTime && targetTime <= nextPointTime) {
        segmentIndex = i;
        break;
      }
    }
    
    if (segmentIndex >= trace.length - 1) {
      const lastPoint = trace[trace.length - 1];
      return { 
        x: lastPoint.position.x, 
        y: lastPoint.position.y,
        confidence: lastPoint.confidence
      };
    }
    
    // Interpolate between the two points
    const p1 = trace[segmentIndex];
    const p2 = trace[segmentIndex + 1];
    
    const t1 = (new Date(p1.created_at) - new Date(trace[0].created_at)) / 1000;
    const t2 = (new Date(p2.created_at) - new Date(trace[0].created_at)) / 1000;
    
    const t = t2 - t1 > 0 ? (targetTime - t1) / (t2 - t1) : 0;
    
    return {
      x: p1.position.x + (p2.position.x - p1.position.x) * t,
      y: p1.position.y + (p2.position.y - p1.position.y) * t,
      confidence: p1.confidence + (p2.confidence - p1.confidence) * t
    };
  }, [traceMode]);

  // Calculate trace path for visualization
  const getTracePath = useMemo(() => {
    if (!traceMode?.isActive || !traceMode.trace || traceMode.trace.length < 2) {
      return null;
    }
    
    const { trace, duration } = traceMode;
    const currentTime = traceMode.currentTime;
    
    // Calculate how far along the trace we are
    const progress = currentTime / duration;
    const visiblePointCount = Math.ceil(trace.length * progress);
    
    // Get visible points
    const visiblePoints = trace.slice(0, Math.max(2, visiblePointCount));
    
    // Build path string
    if (visiblePoints.length < 2) return null;
    
    let path = `M ${visiblePoints[0].position.x} ${visiblePoints[0].position.y}`;
    for (let i = 1; i < visiblePoints.length; i++) {
      path += ` L ${visiblePoints[i].position.x} ${visiblePoints[i].position.y}`;
    }
    
    return path;
  }, [traceMode]);

  const getZoneOffset = useCallback((zoneId) => {
    if (!zoneId) return { dx: 0, dy: 0 };
    const z = zonesById[zoneId];
    const o = z && z.offset ? z.offset : { dx: 0, dy: 0 };
    return { dx: o.dx || 0, dy: o.dy || 0 };
  }, [zonesById]);

  // Compute a non-rectangular warehouse outline that leaves buffer around elements
  const outlinePath = useMemo(() => {
    // If manual outline points are provided, build a rectilinear path from them
    const manual = data?.blueprint?.outlinePoints;
    if (manual && Array.isArray(manual) && manual.length >= 3) {
      const pts = manual.map(p => ({ x: p.x, y: p.y }));
      let d = `M ${pts[0].x} ${pts[0].y}`;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1];
        const b = pts[i];
        if (a.x === b.x || a.y === b.y) {
          d += ` L ${b.x} ${b.y}`;
        } else {
          const corner = { x: a.x, y: b.y }; // vertical then horizontal
          d += ` L ${corner.x} ${corner.y} L ${b.x} ${b.y}`;
        }
      }
      // close with orthogonal bend
      const last = pts[pts.length - 1];
      const first = pts[0];
      if (last.x === first.x || last.y === first.y) {
        d += ` L ${first.x} ${first.y} Z`;
      } else {
        const closeCorner = { x: last.x, y: first.y };
        d += ` L ${closeCorner.x} ${closeCorner.y} L ${first.x} ${first.y} Z`;
      }
      return d;
    }

    // Gather rect-like elements and inflate by buffer to create room around all items
    const buffer = 40; // visual buffer around all elements
    /** @type {{x:number,y:number,width:number,height:number}[]} */
    const rects = [];

    const includeRects = (arr) => {
      if (!arr) return;
      for (const r of arr) {
        rects.push({ x: r.x, y: r.y, width: r.width, height: r.height });
      }
    };

    // Zones that are rectangles (exclude gametime and children's place from main view)
    for (const z of mainViewZones) {
      if (z.rect) rects.push({ x: z.rect.x, y: z.rect.y, width: z.rect.width, height: z.rect.height });
    }
    // Static blueprint rectangles
    includeRects(data.blueprint?.offices);
    includeRects(data.blueprint?.bathrooms);
    includeRects(data.blueprint?.docks);
    includeRects(data.blueprint?.hallways);
    includeRects(data.blueprint?.conferenceRooms);
    // Treat assets as tiny rects for hull purposes (exclude assets from warehouse-like zones)
    for (const a of data.assets) {
      // Skip assets without a valid position
      if (!a.position) continue;
      const zone = zonesById[a.zoneId];
      // Exclude assets from zones that are actually warehouses (have GPS polygons)
      if (zone && zone.gps && zone.gps.polygon) continue;
      rects.push({ x: a.position.x, y: a.position.y, width: 0, height: 0 });
    }

    if (rects.length === 0) return '';

    // Generate points from inflated rectangles
    /** @type {{x:number,y:number}[]} */
    const points = [];
    for (const r of rects) {
      const x = r.x - buffer;
      const y = r.y - buffer;
      const w = r.width + buffer * 2;
      const h = r.height + buffer * 2;
      points.push({ x: x, y: y });
      points.push({ x: x + w, y: y });
      points.push({ x: x + w, y: y + h });
      points.push({ x: x, y: y + h });
    }

    // Monotone chain convex hull
    const byXY = points
      .slice()
      .sort((a, b) => (a.x === b.x ? a.y - b.y : a.x - b.x));

    const cross = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);

    const lower = [];
    for (const p of byXY) {
      while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
        lower.pop();
      }
      lower.push(p);
    }
    const upper = [];
    for (let i = byXY.length - 1; i >= 0; i--) {
      const p = byXY[i];
      while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
        upper.pop();
      }
      upper.push(p);
    }
    upper.pop();
    lower.pop();
    const hull = lower.concat(upper);
    if (hull.length < 3) return '';

    // Build rectilinear (Manhattan) SVG path: replace each edge with vertical + horizontal
    let d = `M ${hull[0].x} ${hull[0].y}`;
    for (let i = 1; i < hull.length; i++) {
      const a = hull[i - 1];
      const b = hull[i];
      const corner = { x: a.x, y: b.y }; // vertical then horizontal bend
      d += ` L ${corner.x} ${corner.y} L ${b.x} ${b.y}`;
    }
    // close back to start with orthogonal bend
    const last = hull[hull.length - 1];
    const first = hull[0];
    const closeCorner = { x: last.x, y: first.y };
    d += ` L ${closeCorner.x} ${closeCorner.y} L ${first.x} ${first.y} Z`;
    return d;
  }, [data, mainViewZones]);

  // Compute default viewBox to auto-fit blueprint with buffer
  const defaultViewBox = useMemo(() => {
    // First, calculate bounds from blueprint outlinePoints if available
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    let hasBlueprintBounds = false;

    if (data.blueprint?.outlinePoints && data.blueprint.outlinePoints.length > 0) {
      // Calculate bounds from blueprint outline
      for (const pt of data.blueprint.outlinePoints) {
        minX = Math.min(minX, pt.x);
        minY = Math.min(minY, pt.y);
        maxX = Math.max(maxX, pt.x);
        maxY = Math.max(maxY, pt.y);
      }
      hasBlueprintBounds = true;
    }

    // If no blueprint bounds, fall back to zones and other elements
    if (!hasBlueprintBounds) {
      minX = 0;
      minY = 0;
      maxX = 800;
      maxY = 600;
    }

    // Include zones by rect (include zone-level offsets)
    for (const z of mainViewZones) {
      if (z.rect) {
        const dx = z.offset?.dx || 0;
        const dy = z.offset?.dy || 0;
        minX = Math.min(minX, z.rect.x + dx);
        minY = Math.min(minY, z.rect.y + dy);
        maxX = Math.max(maxX, z.rect.x + dx + z.rect.width);
        maxY = Math.max(maxY, z.rect.y + dy + z.rect.height);
      }
    }

    const includeRects = (arr) => {
      if (!arr) return;
      for (const r of arr) {
        minX = Math.min(minX, r.x);
        minY = Math.min(minY, r.y);
        maxX = Math.max(maxX, r.x + r.width);
        maxY = Math.max(maxY, r.y + r.height);
      }
    };
    includeRects(data.blueprint?.offices);
    includeRects(data.blueprint?.bathrooms);
    includeRects(data.blueprint?.docks);
    includeRects(data.blueprint?.hallways);

    // Calculate blueprint dimensions
    const blueprintWidth = maxX - minX;
    const blueprintHeight = maxY - minY;

    // Get SVG container dimensions (accounting for sidebar and top buttons)
    // Sidebar is typically ~380px wide, top buttons area is ~60-80px tall
    // We'll use a percentage-based buffer that works for different screen sizes
    const bufferPercent = 0.1; // 10% buffer on all sides
    const bufferX = blueprintWidth * bufferPercent;
    const bufferY = blueprintHeight * bufferPercent;

    // Calculate viewBox with buffer
    const x = minX - bufferX;
    const y = minY - bufferY;
    const w = blueprintWidth + (bufferX * 2);
    const h = blueprintHeight + (bufferY * 2);

    return `${x} ${y} ${w} ${h}`;
  }, [data, mainViewZones]);

  // Compute the "logical" viewBox from navigation state (zone selection, etc.)
  const logicalViewBox = useMemo(() => {
    if (selectedRackZoneId && zonesById[selectedRackZoneId]) {
      const z = zonesById[selectedRackZoneId];
      if (z.rect) {
        const pad = 20;
        const dx = z.offset?.dx || 0;
        const dy = z.offset?.dy || 0;
        return `${z.rect.x + dx - pad} ${z.rect.y + dy - pad} ${z.rect.width + pad * 2} ${z.rect.height + pad * 2}`;
      }
      return z.viewBox;
    }
    if (activeView.type === 'zone' && activeView.id && zonesById[activeView.id]) {
      const z = zonesById[activeView.id];
      if (z.rect) {
        const pad = 20;
        const dx = z.offset?.dx || 0;
        const dy = z.offset?.dy || 0;
        return `${z.rect.x + dx - pad} ${z.rect.y + dy - pad} ${z.rect.width + pad * 2} ${z.rect.height + pad * 2}`;
      }
      return z.viewBox;
    }
    return defaultViewBox;
  }, [activeView, zonesById, defaultViewBox, selectedRackZoneId]);

  // Final viewBox: pan/zoom overrides the logical view but never touches activeView
  const viewBox = panZoomViewBox ?? logicalViewBox;

  // Keep refs in sync with latest state so stable handlers always read fresh values
  viewBoxRef.current = viewBox;
  editModeRef.current = editMode;
  blueprintEditModeRef.current = blueprintEditMode;
  buildModeRef.current = buildMode;
  hubPlacementModeRef.current = hubPlacementMode;
  addCornerModeRef.current = addCornerMode;
  dragStateRef.current = dragState;


  useEffect(() => {
    if (selectedZoneId) setActiveView({ type: 'zone', id: selectedZoneId });
  }, [selectedZoneId]);

  useEffect(() => {
    if (selectedAssetId) setActiveView((v) => (v.type === 'zone' ? v : { type: 'asset', id: selectedAssetId }));
  }, [selectedAssetId]);


  const resetToFull = () => {
    setActiveView({ type: 'default', id: null });
    setPanZoomViewBox(null); // also clear any pan/zoom override
    if (onZoneSelect) onZoneSelect(null);
    if (onRackSelect) onRackSelect(null);
    if (onAssetSelect) onAssetSelect(null);
  };


  // Center to selected external asset or zone when in Google view
  const googleCenter = useMemo(() => {
    if (useGoogleView && selectedZoneId) {
      const zone = zonesById[selectedZoneId];
      if (zone && zone.gps && zone.gps.polygon) {
        // Calculate center of zone polygon
        const coords = zone.gps.polygon;
        const latSum = coords.reduce((sum, c) => sum + c.lat, 0);
        const lngSum = coords.reduce((sum, c) => sum + c.lng, 0);
        return { lat: latSum / coords.length, lng: lngSum / coords.length };
      }
    }
    if (useGoogleView && selectedAssetId) {
      const hit = externalAssets.find(a => a.id === selectedAssetId);
      if (hit) return { lat: hit.lat, lng: hit.lng };
    }
    return externalWarehouseCenter;
  }, [useGoogleView, selectedZoneId, selectedAssetId, externalAssets, externalWarehouseCenter, zonesById]);

  // Zoom level for Google view based on selection
  const googleZoom = useMemo(() => {
    if (useGoogleView && selectedZoneId) return 17; // Zoom in to zone
    if (useGoogleView && selectedAssetId) return 20; // Zoom in to asset
    return 20; // Default view shows G2 Supply at close zoom
  }, [useGoogleView, selectedZoneId, selectedAssetId]);

  // Finish Build Handler
  const handleFinishBuild = async () => {
    if (buildPoints.length < 3) {
      return;
    }

    // 1. Find GPS bounding box (Top-Left origin)
    let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
    buildPoints.forEach(p => {
      minLat = Math.min(minLat, p.lat);
      maxLat = Math.max(maxLat, p.lat);
      minLng = Math.min(minLng, p.lng);
      maxLng = Math.max(maxLng, p.lng);
    });

    // Origin is Top-Left (North-West)
    const origin = { lat: maxLat, lng: minLng };
    const METERS_PER_DEG_LAT = 111320;
    const METERS_PER_DEG_LNG = 111320 * Math.cos(origin.lat * Math.PI / 180);

    // 2. Convert to meters (SVG coordinates)
    const outlinePoints = buildPoints.map(p => ({
      x: Math.round((p.lng - origin.lng) * METERS_PER_DEG_LNG),
      y: Math.round((origin.lat - p.lat) * METERS_PER_DEG_LAT) // Invert Lat so Y increases downwards
    }));

    // 3. Compute bounding rect
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    outlinePoints.forEach(p => {
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x);
      maxY = Math.max(maxY, p.y);
    });

    const rect = {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };

    const result = {
      gps: { polygon: buildPoints },
      blueprint: { outlinePoints },
      org_id: selectedOrgId || null, // Link to selected organization
      zones: [{
          id: 'zone-main',
          name: 'Main Zone',
          rect, // Initial zone fills the whole building
          color: 'rgba(59, 130, 246, 0.25)',
          borderColor: 'rgb(59, 130, 246)',
          gps: { polygon: buildPoints }
      }]
    };

    if (onCreateWarehouse) {
      const name = prompt("Enter a name for your new warehouse:", "New Warehouse");
      if (!name) return;
      
      // Check if warehouse already exists
      if (checkWarehouseExists) {
        const existing = await checkWarehouseExists(name);
        if (existing) {
          // Show conflict dialog
          setConflictDialog({
            name,
            existingWarehouse: existing,
            layoutData: result
          });
          return; // Don't proceed until user chooses
        }
      }
      
      // No conflict, create normally
      const newId = await onCreateWarehouse(name, result);
      if (newId) {
        setUseGoogleView(false);
        setBuildMode(false);
        setBuildPoints([]);
        setEditMode(true);
      }
    } else {
      console.log("Result:", result);
    }
  };

  // Handle conflict dialog actions
  const handleOverride = async () => {
    if (!conflictDialog) return;
    const { name, existingWarehouse, layoutData } = conflictDialog;
    
    const newId = await onCreateWarehouse(name, layoutData, {
      override: true,
      existingWarehouseId: existingWarehouse.id
    });
    
    if (newId) {
      setConflictDialog(null);
      setUseGoogleView(false);
      setBuildMode(false);
      setBuildPoints([]);
      setEditMode(true);
    }
  };

  const handleUpdate = async () => {
    if (!conflictDialog) return;
    const { name, existingWarehouse, layoutData } = conflictDialog;
    
    const newId = await onCreateWarehouse(name, layoutData, {
      update: true,
      existingWarehouseId: existingWarehouse.id
    });
    
    if (newId) {
      setConflictDialog(null);
      setUseGoogleView(false);
      setBuildMode(false);
      setBuildPoints([]);
      setEditMode(true);
    }
  };

  const handleCancelConflict = () => {
    setConflictDialog(null);
    setBuildPoints([]);
  };

  // --- GPS Edit Mode Handlers ---
  
  // Save current state to history (for undo/redo)
  const saveBuildPointsToHistory = (newPoints) => {
    // Truncate any redo history when making a new change
    const newHistory = buildPointsHistory.slice(0, buildPointsHistoryIndex + 1);
    newHistory.push([...newPoints]);
    setBuildPointsHistory(newHistory);
    setBuildPointsHistoryIndex(newHistory.length - 1);
  };

  // Add a new point (with history)
  const handleAddBuildPoint = (point) => {
    const newPoints = [...buildPoints, point];
    setBuildPoints(newPoints);
    saveBuildPointsToHistory(newPoints);
    setSelectedPointIndex(null);
  };

  // Load warehouse GPS polygon for editing
  const handleSelectWarehouseForGPSEdit = (warehouseId) => {
    const warehouse = warehouses[warehouseId];
    if (warehouse?.gps?.polygon) {
      setEditingWarehouseId(warehouseId);
      const points = [...warehouse.gps.polygon];
      setBuildPoints(points);
      setBuildPointsHistory([points]);
      setBuildPointsHistoryIndex(0);
      setSelectedPointIndex(null);
    }
  };

  // Handle marker drag in edit mode (with history on drag end)
  const handleBuildPointUpdate = (index, newPosition, saveToHistory = false) => {
    setBuildPoints(prev => {
      const updated = [...prev];
      updated[index] = newPosition;
      if (saveToHistory) {
        saveBuildPointsToHistory(updated);
      }
      return updated;
    });
  };

  // Delete a specific point
  const handleDeleteBuildPoint = (index) => {
    if (buildPoints.length <= 3) {
      return;
    }
    const newPoints = buildPoints.filter((_, i) => i !== index);
    setBuildPoints(newPoints);
    saveBuildPointsToHistory(newPoints);
    setSelectedPointIndex(null);
  };

  // Undo build points
  const handleBuildPointsUndo = () => {
    if (buildPointsHistoryIndex > 0) {
      const newIndex = buildPointsHistoryIndex - 1;
      setBuildPointsHistoryIndex(newIndex);
      setBuildPoints([...buildPointsHistory[newIndex]]);
      setSelectedPointIndex(null);
    }
  };

  // Redo build points
  const handleBuildPointsRedo = () => {
    if (buildPointsHistoryIndex < buildPointsHistory.length - 1) {
      const newIndex = buildPointsHistoryIndex + 1;
      setBuildPointsHistoryIndex(newIndex);
      setBuildPoints([...buildPointsHistory[newIndex]]);
      setSelectedPointIndex(null);
    }
  };

  // Check if undo/redo is available
  const canUndoBuildPoints = buildPointsHistoryIndex > 0;
  const canRedoBuildPoints = buildPointsHistoryIndex < buildPointsHistory.length - 1;

  // Clear build points with history reset
  const handleClearBuildPoints = () => {
    setBuildPoints([]);
    setBuildPointsHistory([]);
    setBuildPointsHistoryIndex(-1);
    setSelectedPointIndex(null);
  };

  // Save GPS edits
  const handleSaveGPSEdit = async () => {
    if (!editingWarehouseId || buildPoints.length < 3) return;
    
    if (onUpdateWarehouseGPS) {
      const success = await onUpdateWarehouseGPS(editingWarehouseId, buildPoints);
      if (success) {
        setBuildMode(false);
        handleClearBuildPoints();
        setEditingWarehouseId(null);
        setGpsBuildSubMode('create');
      }
    }
  };

  // Cancel GPS edit
  const handleCancelGPSEdit = () => {
    handleClearBuildPoints();
    setEditingWarehouseId(null);
    setGpsBuildSubMode('create');
  };

  // Get list of warehouses with GPS polygons for editing
  const warehousesWithGPS = useMemo(() => {
    return Object.values(warehouses).filter(wh => wh.gps && wh.gps.polygon && wh.gps.polygon.length >= 3);
  }, [warehouses]);

  // --- Edit Mode Logic ---
  const handleSvgMouseMove = (e) => {
    if ((!editMode && !blueprintEditMode) || !dragState) return;
    const svg = e.currentTarget;
    
    let svgP = { x: e.clientX, y: e.clientY };
    try {
      const pt = svg.createSVGPoint();
      pt.x = e.clientX;
      pt.y = e.clientY;
      svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    } catch (err) { return; }

    const newX = dragState.origX + (svgP.x - dragState.startSvgX);
    const newY = dragState.origY + (svgP.y - dragState.startSvgY);

    if (dragState.type === 'zone' && editMode) {
      // Only update local state for smooth dragging - don't trigger database updates
      setDraggingZonePosition({
        id: dragState.id,
        x: Math.round(newX),
        y: Math.round(newY),
        width: dragState.origWidth || data.zones.find(z => z.id === dragState.id)?.rect?.width || 200,
        height: dragState.origHeight || data.zones.find(z => z.id === dragState.id)?.rect?.height || 120
      });
    } else if (dragState.type === 'zone-resize' && editMode) {
      const deltaX = svgP.x - dragState.startSvgX;
      const deltaY = svgP.y - dragState.startSvgY;
      const handle = dragState.handle;
      const MIN_SIZE = 50; // Minimum zone size
      
      let newX = dragState.origX;
      let newY = dragState.origY;
      let newWidth = dragState.origWidth;
      let newHeight = dragState.origHeight;
      
      // Handle different resize directions
      if (handle.includes('w')) {
        newX = dragState.origX + deltaX;
        newWidth = Math.max(MIN_SIZE, dragState.origWidth - deltaX);
        if (newWidth === MIN_SIZE) newX = dragState.origX + dragState.origWidth - MIN_SIZE;
      }
      if (handle.includes('e')) {
        newWidth = Math.max(MIN_SIZE, dragState.origWidth + deltaX);
      }
      if (handle.includes('n')) {
        newY = dragState.origY + deltaY;
        newHeight = Math.max(MIN_SIZE, dragState.origHeight - deltaY);
        if (newHeight === MIN_SIZE) newY = dragState.origY + dragState.origHeight - MIN_SIZE;
      }
      if (handle.includes('s')) {
        newHeight = Math.max(MIN_SIZE, dragState.origHeight + deltaY);
      }
      
      // Only update local state for smooth resizing - don't trigger database updates
      setDraggingZonePosition({
        id: dragState.id,
        x: Math.round(newX),
        y: Math.round(newY),
        width: Math.round(newWidth),
        height: Math.round(newHeight)
      });
    } else if (dragState.type === 'vertex' && onBlueprintUpdate && blueprintEditMode) {
      const points = [...(data.blueprint.outlinePoints || [])];
      const MIN_DISTANCE = 20; // Minimum distance between corners in pixels
      
      // Round to nearest integer for exact positioning
      let constrainedX = Math.round(newX);
      let constrainedY = Math.round(newY);
      
      // Check distance to adjacent corners (prev and next)
      const prevIndex = (dragState.id - 1 + points.length) % points.length;
      const nextIndex = (dragState.id + 1) % points.length;
      
      const prevPoint = points[prevIndex];
      const nextPoint = points[nextIndex];
      
      // Calculate distances to adjacent points
      const distToPrev = Math.sqrt(
        Math.pow(constrainedX - prevPoint.x, 2) + 
        Math.pow(constrainedY - prevPoint.y, 2)
      );
      const distToNext = Math.sqrt(
        Math.pow(constrainedX - nextPoint.x, 2) + 
        Math.pow(constrainedY - nextPoint.y, 2)
      );
      
      // If too close to previous point, constrain along the line from prev to next
      if (distToPrev < MIN_DISTANCE) {
        const dx = nextPoint.x - prevPoint.x;
        const dy = nextPoint.y - prevPoint.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        if (length > 0) {
          // Move to minimum distance along the line
          const ratio = MIN_DISTANCE / length;
          constrainedX = Math.round(prevPoint.x + dx * ratio);
          constrainedY = Math.round(prevPoint.y + dy * ratio);
        }
      }
      
      // If too close to next point, constrain along the line from prev to next
      if (distToNext < MIN_DISTANCE) {
        const dx = nextPoint.x - prevPoint.x;
        const dy = nextPoint.y - prevPoint.y;
        const length = Math.sqrt(dx * dx + dy * dy);
        if (length > 0) {
          // Move to minimum distance from next point
          const ratio = 1 - (MIN_DISTANCE / length);
          constrainedX = Math.round(prevPoint.x + dx * ratio);
          constrainedY = Math.round(prevPoint.y + dy * ratio);
        }
      }
      
      // Check distance to all other points (not adjacent) to prevent overlap
      for (let i = 0; i < points.length; i++) {
        if (i === dragState.id || i === prevIndex || i === nextIndex) continue;
        
        const otherPoint = points[i];
        const dist = Math.sqrt(
          Math.pow(constrainedX - otherPoint.x, 2) + 
          Math.pow(constrainedY - otherPoint.y, 2)
        );
        
        // If too close to another point, push away from it
        if (dist < MIN_DISTANCE && dist > 0) {
          const pushX = (constrainedX - otherPoint.x) / dist;
          const pushY = (constrainedY - otherPoint.y) / dist;
          constrainedX = Math.round(otherPoint.x + pushX * MIN_DISTANCE);
          constrainedY = Math.round(otherPoint.y + pushY * MIN_DISTANCE);
        }
      }
      
      // Update the dragged point with constrained position
      points[dragState.id] = { x: constrainedX, y: constrainedY };
      // Don't save to history on every drag - only on mouse up
      onBlueprintUpdate({ ...data.blueprint, outlinePoints: points });
    }
  };

  const handleZoneMouseDown = (e, zone) => {
    if (!editMode || blueprintEditMode || !zone.rect) return;
    e.stopPropagation();
    const svg = e.target.closest('svg');
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

    setDragState({
        type: 'zone',
        id: zone.id,
        startSvgX: svgP.x,
        startSvgY: svgP.y,
        origX: zone.rect.x,
        origY: zone.rect.y,
        origWidth: zone.rect.width,
        origHeight: zone.rect.height
    });
    
    // Initialize dragging position
    setDraggingZonePosition({
      id: zone.id,
      x: zone.rect.x,
      y: zone.rect.y,
      width: zone.rect.width,
      height: zone.rect.height
    });
  };

  // Handle zone resize from corner/edge handles
  const handleZoneResizeMouseDown = (e, zone, handle) => {
    if (!editMode || blueprintEditMode || !zone.rect) return;
    e.stopPropagation();
    const svg = e.target.closest('svg');
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

    setDragState({
        type: 'zone-resize',
        id: zone.id,
        handle: handle, // 'nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'
        startSvgX: svgP.x,
        startSvgY: svgP.y,
        origX: zone.rect.x,
        origY: zone.rect.y,
        origWidth: zone.rect.width,
        origHeight: zone.rect.height
    });
    
    // Initialize dragging position
    setDraggingZonePosition({
      id: zone.id,
      x: zone.rect.x,
      y: zone.rect.y,
      width: zone.rect.width,
      height: zone.rect.height
    });
  };

  const handleVertexMouseDown = (e, index, point) => {
    if (!blueprintEditMode) return;
    e.stopPropagation();
    const svg = e.target.closest('svg');
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());

    setDragState({
        type: 'vertex',
        id: index,
        startSvgX: svgP.x,
        startSvgY: svgP.y,
        origX: point.x,
        origY: point.y
    });
  };

  // ─── Pan & Zoom ──────────────────────────────────────────────────────────────
  // All handlers are created once (empty dep array) and read live state via refs.
  // This prevents stale closures AND avoids re-registering the native wheel
  // listener on every render (which caused missed events and jitter).

  const handleWheel = useCallback((e) => {
    if (editModeRef.current || blueprintEditModeRef.current ||
        buildModeRef.current || hubPlacementModeRef.current || addCornerModeRef.current) return;

    e.preventDefault();
    e.stopPropagation();

    const current = viewBoxRef.current;
    if (!current) return;
    const [vx, vy, vw, vh] = current.split(' ').map(Number);

    const container = mapContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Normalise delta across mouse wheels and trackpads
    let delta = e.deltaY;
    if (e.deltaMode === 1) delta *= 20;   // line mode → pixels
    if (e.deltaMode === 2) delta *= 300;  // page mode → pixels
    // Clamp so a single fast scroll doesn't jump too far
    delta = Math.max(-100, Math.min(100, delta));

    const zoomFactor = Math.pow(1.001, -delta); // smooth exponential zoom

    // SVG coordinate under the cursor
    const svgX = vx + (mouseX / rect.width) * vw;
    const svgY = vy + (mouseY / rect.height) * vh;

    const newWidth  = vw / zoomFactor;
    const newHeight = vh / zoomFactor;
    const newX = svgX - (mouseX / rect.width)  * newWidth;
    const newY = svgY - (mouseY / rect.height) * newHeight;

    setPanZoomViewBox(`${newX} ${newY} ${newWidth} ${newHeight}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable – reads all state via refs

  const handlePanStart = useCallback((e) => {
    if (editModeRef.current || blueprintEditModeRef.current ||
        buildModeRef.current || hubPlacementModeRef.current ||
        addCornerModeRef.current || dragStateRef.current) return;

    // Only start pan on SVG background elements
    const tag = e.target.tagName;
    if (tag !== 'svg' && tag !== 'rect' && !e.target.classList.contains('pan-target')) return;

    isPanningRef.current = true;
    panStartRef.current = { x: e.clientX, y: e.clientY };
    e.currentTarget.style.cursor = 'grabbing';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable

  const handlePanMove = useCallback((e) => {
    if (!isPanningRef.current) return;

    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;
    panStartRef.current = { x: e.clientX, y: e.clientY };

    const current = viewBoxRef.current;
    if (!current) return;
    const [vx, vy, vw, vh] = current.split(' ').map(Number);

    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;

    const svgDx = (dx / rect.width)  * vw;
    const svgDy = (dy / rect.height) * vh;

    setPanZoomViewBox(`${vx - svgDx} ${vy - svgDy} ${vw} ${vh}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable

  const handlePanEnd = useCallback((e) => {
    if (!isPanningRef.current) return;
    isPanningRef.current = false;
    if (mapContainerRef.current) mapContainerRef.current.style.cursor = '';
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // stable

  // Attach wheel as a native non-passive listener (once, on mount)
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []); // intentionally empty – handler is stable

  // Attach pan move/end to document (once, on mount)
  useEffect(() => {
    document.addEventListener('mousemove', handlePanMove);
    document.addEventListener('mouseup', handlePanEnd);
    return () => {
      document.removeEventListener('mousemove', handlePanMove);
      document.removeEventListener('mouseup', handlePanEnd);
    };
  }, []); // intentionally empty – handlers are stable

  const handleMouseUp = () => {
    if (dragState) {
      // Save to history when drag ends
      if (dragState.type === 'vertex' && blueprintEditMode && data.blueprint) {
        // Ensure all corners are exact integers before saving
        const points = data.blueprint.outlinePoints || [];
        const exactPoints = points.map(p => ({
          x: Math.round(p.x),
          y: Math.round(p.y)
        }));
        
        // Update with exact positions
        if (onBlueprintUpdate) {
          onBlueprintUpdate({ ...data.blueprint, outlinePoints: exactPoints });
        }
        
        // Save exact version to history
        saveBlueprintToHistory({ ...data.blueprint, outlinePoints: exactPoints });
      } else if ((dragState.type === 'zone' || dragState.type === 'zone-resize') && editMode && data.zones && draggingZonePosition) {
        // Apply the final dragged position to the zones
        const updatedZones = data.zones.map(z => {
          if (z.id === draggingZonePosition.id) {
            return {
              ...z,
              rect: {
                x: draggingZonePosition.x,
                y: draggingZonePosition.y,
                width: draggingZonePosition.width,
                height: draggingZonePosition.height
              }
            };
          }
          return z;
        });
        
        // Save to history before updating
        saveZonesToHistory(data.zones);
        
        // Update database
        if (onZoneUpdate) {
          onZoneUpdate(updatedZones);
        }
      }
    }
    setDragState(null);
    // Always clear dragging position when drag ends
    setDraggingZonePosition(null);
  };

  // Save state to history before making changes
  const saveBlueprintToHistory = useCallback((blueprint) => {
    setBlueprintHistory(prev => {
      const newHistory = prev.slice(0, blueprintHistoryIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(blueprint))); // Deep clone
      // Limit history to 50 entries
      if (newHistory.length > 50) newHistory.shift();
      else setBlueprintHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [blueprintHistoryIndex]);

  const saveZonesToHistory = useCallback((zones) => {
    setZoneHistory(prev => {
      const newHistory = prev.slice(0, zoneHistoryIndex + 1);
      newHistory.push(JSON.parse(JSON.stringify(zones))); // Deep clone
      // Limit history to 50 entries
      if (newHistory.length > 50) newHistory.shift();
      else setZoneHistoryIndex(newHistory.length - 1);
      return newHistory;
    });
  }, [zoneHistoryIndex]);

  // Undo/Redo functions
  const handleUndo = useCallback(() => {
    if (blueprintEditMode && blueprintHistoryIndex > 0) {
      const prevBlueprint = blueprintHistory[blueprintHistoryIndex - 1];
      setBlueprintHistoryIndex(prev => prev - 1);
      if (onBlueprintUpdate) {
        onBlueprintUpdate(prevBlueprint);
      }
    } else if (editMode && zoneHistoryIndex > 0) {
      const prevZones = zoneHistory[zoneHistoryIndex - 1];
      setZoneHistoryIndex(prev => prev - 1);
      if (onZoneUpdate) {
        onZoneUpdate(prevZones);
      }
    }
  }, [blueprintEditMode, blueprintHistoryIndex, blueprintHistory, editMode, zoneHistoryIndex, zoneHistory, onBlueprintUpdate, onZoneUpdate]);

  const handleRedo = useCallback(() => {
    if (blueprintEditMode && blueprintHistoryIndex < blueprintHistory.length - 1) {
      const nextBlueprint = blueprintHistory[blueprintHistoryIndex + 1];
      setBlueprintHistoryIndex(prev => prev + 1);
      if (onBlueprintUpdate) {
        onBlueprintUpdate(nextBlueprint);
      }
    } else if (editMode && zoneHistoryIndex < zoneHistory.length - 1) {
      const nextZones = zoneHistory[zoneHistoryIndex + 1];
      setZoneHistoryIndex(prev => prev + 1);
      if (onZoneUpdate) {
        onZoneUpdate(nextZones);
      }
    }
  }, [blueprintEditMode, blueprintHistoryIndex, blueprintHistory, editMode, zoneHistoryIndex, zoneHistory, onBlueprintUpdate, onZoneUpdate]);

  // Check if undo/redo is available
  const canUndo = blueprintEditMode 
    ? blueprintHistoryIndex > 0 
    : editMode 
      ? zoneHistoryIndex > 0 
      : false;
  const canRedo = blueprintEditMode 
    ? blueprintHistoryIndex < blueprintHistory.length - 1 
    : editMode 
      ? zoneHistoryIndex < zoneHistory.length - 1 
      : false;

  // Initialize history when entering edit mode
  useEffect(() => {
    if (blueprintEditMode && data.blueprint) {
      setBlueprintHistory([JSON.parse(JSON.stringify(data.blueprint))]);
      setBlueprintHistoryIndex(0);
    }
  }, [blueprintEditMode]); // Only when entering mode

  useEffect(() => {
    if (editMode && data.zones) {
      setZoneHistory([JSON.parse(JSON.stringify(data.zones))]);
      setZoneHistoryIndex(0);
    }
  }, [editMode]); // Only when entering mode

  // Add corner to blueprint outline
  const handleAddCornerToBlueprint = useCallback((e) => {
    if (!blueprintEditMode || !addCornerMode || !data.blueprint?.outlinePoints) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const svg = e.currentTarget.closest('svg');
    if (!svg) return;
    
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    
    // Round to exact integer position
    const newX = Math.round(svgP.x);
    const newY = Math.round(svgP.y);
    const MIN_DISTANCE = 20; // Minimum distance from existing corners
    
    // Check if new point is too close to any existing corner
    const points = data.blueprint.outlinePoints;
    for (const point of points) {
      const dist = Math.sqrt(Math.pow(newX - point.x, 2) + Math.pow(newY - point.y, 2));
      if (dist < MIN_DISTANCE) {
        return;
      }
    }
    
    // Find the closest edge to insert the point
    let minDist = Infinity;
    let insertIndex = points.length;
    let insertPoint = { x: newX, y: newY };
    
    for (let i = 0; i < points.length; i++) {
      const p1 = points[i];
      const p2 = points[(i + 1) % points.length];
      
      // Distance from point to line segment
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length === 0) continue;
      
      const t = Math.max(0, Math.min(1, ((newX - p1.x) * dx + (newY - p1.y) * dy) / (length * length)));
      const projX = p1.x + t * dx;
      const projY = p1.y + t * dy;
      const dist = Math.sqrt((newX - projX) ** 2 + (newY - projY) ** 2);
      
      // Only consider if within 50 units and not too close to endpoints
      const distToP1 = Math.sqrt(Math.pow(newX - p1.x, 2) + Math.pow(newY - p1.y, 2));
      const distToP2 = Math.sqrt(Math.pow(newX - p2.x, 2) + Math.pow(newY - p2.y, 2));
      
      if (dist < minDist && dist < 50 && distToP1 >= MIN_DISTANCE && distToP2 >= MIN_DISTANCE) {
        minDist = dist;
        insertIndex = i + 1;
        // Use projected point on edge for exact corner placement
        insertPoint = { x: Math.round(projX), y: Math.round(projY) };
      }
    }
    
    if (insertIndex <= points.length && minDist < 50) {
      const newPoints = [...points];
      newPoints.splice(insertIndex, 0, insertPoint);
      
      saveBlueprintToHistory(data.blueprint);
      if (onBlueprintUpdate) {
        onBlueprintUpdate({ ...data.blueprint, outlinePoints: newPoints });
      }
      setAddCornerMode(false);
    }
  }, [blueprintEditMode, addCornerMode, data.blueprint, onBlueprintUpdate, saveBlueprintToHistory]);

  // Delete corner from blueprint
  const handleDeleteCorner = useCallback((index) => {
    if (!blueprintEditMode || !data.blueprint?.outlinePoints) return;
    
    const points = data.blueprint.outlinePoints;
    if (points.length <= 3) {
      return;
    }
    
    const newPoints = points.filter((_, i) => i !== index);
    saveBlueprintToHistory(data.blueprint);
    if (onBlueprintUpdate) {
      onBlueprintUpdate({ ...data.blueprint, outlinePoints: newPoints });
    }
    setContextMenu(null);
  }, [blueprintEditMode, data.blueprint, onBlueprintUpdate, saveBlueprintToHistory]);

  // Handle right-click context menu
  const handleVertexContextMenu = useCallback((e, index) => {
    if (!blueprintEditMode) return;
    e.preventDefault();
    e.stopPropagation();
    
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      type: 'vertex',
      index
    });
  }, [blueprintEditMode]);

  // Close context menu when clicking elsewhere
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', handleClick);
      return () => document.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  // --- Asset Dragging Handlers ---
  const handleAssetDragStart = useCallback((e, asset) => {
    if (!editMode) return;
    e.stopPropagation();
    
    const svg = e.currentTarget.closest('svg');
    if (!svg) return;
    
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    
    setDraggingAsset({
      id: asset.id,
      startX: svgP.x,
      startY: svgP.y,
      currentX: asset.position.x,
      currentY: asset.position.y,
      originalPosition: { ...asset.position }
    });
  }, [editMode]);

  const handleAssetDrag = useCallback((e) => {
    if (!draggingAsset) return;
    
    // Find SVG element - try multiple methods for robustness
    let svg = null;
    if (e.target && e.target.closest) {
      svg = e.target.closest('svg');
    } else if (e.currentTarget && e.currentTarget.closest) {
      svg = e.currentTarget.closest('svg');
    }
    
    // Fallback: find SVG by querySelector
    if (!svg) {
      svg = document.querySelector('svg');
    }
    
    if (!svg) return;
    
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    
    const dx = svgP.x - draggingAsset.startX;
    const dy = svgP.y - draggingAsset.startY;
    
    setDraggingAsset(prev => ({
      ...prev,
      currentX: prev.originalPosition.x + dx,
      currentY: prev.originalPosition.y + dy
    }));
  }, [draggingAsset]);

  // Helper function to check if a point is inside a zone's rect
  const isPointInZone = useCallback((position, zone) => {
    if (!position || !position.x || !position.y || !zone.rect) return false;
    const { x, y, width, height } = zone.rect;
    const dx = zone.offset?.dx || 0;
    const dy = zone.offset?.dy || 0;
    const zoneX = x + dx;
    const zoneY = y + dy;
    return position.x >= zoneX && position.x <= zoneX + width &&
           position.y >= zoneY && position.y <= zoneY + height;
  }, []);

  const handleAssetDragEnd = useCallback(async () => {
    if (!draggingAsset) return;
    
    const newPosition = {
      x: Math.round(draggingAsset.currentX),
      y: Math.round(draggingAsset.currentY)
    };
    
    // Detect which zone the asset is now in based on position
    let detectedZoneId = null;
    for (const zone of mainViewZones) {
      if (isPointInZone(newPosition, zone)) {
        detectedZoneId = zone.id;
        break;
      }
    }
    
    // Call the position update callback with detected zone
    if (onAssetPositionUpdate) {
      try {
        await onAssetPositionUpdate(draggingAsset.id, newPosition, detectedZoneId);
      } catch (err) {
        console.error('Failed to update asset position:', err);
      }
    }
    
    setDraggingAsset(null);
  }, [draggingAsset, onAssetPositionUpdate, mainViewZones, isPointInZone]);

  // --- Hub Dragging Handlers ---
  const handleHubDragStart = useCallback((e, hub) => {
    if (!editMode) return;
    if (!hub || !hub.position) {
      console.warn('Cannot drag hub without position:', hub);
      return;
    }
    e.stopPropagation();
    
    const svg = e.currentTarget.closest('svg');
    if (!svg) return;
    
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    
    setDraggingHub({
      id: hub.id,
      startX: svgP.x,
      startY: svgP.y,
      currentX: hub.position.x,
      currentY: hub.position.y,
      originalPosition: { x: hub.position.x, y: hub.position.y }
    });
    setSelectedHubId(hub.id);
  }, [editMode]);

  const handleHubDrag = useCallback((e) => {
    if (!draggingHub || !draggingHub.originalPosition) return;
    
    // Find SVG element - try multiple methods for robustness
    let svg = null;
    if (e.target && e.target.closest) {
      svg = e.target.closest('svg');
    } else if (e.currentTarget && e.currentTarget.closest) {
      svg = e.currentTarget.closest('svg');
    }
    
    // Fallback: find SVG by querySelector
    if (!svg) {
      svg = document.querySelector('svg');
    }
    
    if (!svg) return;
    
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    
    const dx = svgP.x - draggingHub.startX;
    const dy = svgP.y - draggingHub.startY;
    
    setDraggingHub(prev => {
      if (!prev || !prev.originalPosition) return prev;
      return {
        ...prev,
        currentX: prev.originalPosition.x + dx,
        currentY: prev.originalPosition.y + dy
      };
    });
  }, [draggingHub]);

  const handleHubDragEnd = useCallback(async () => {
    if (!draggingHub) return;
    
    const newPosition = {
      x: Math.round(draggingHub.currentX || 0),
      y: Math.round(draggingHub.currentY || 0)
    };
    
    if (onHubPositionUpdate && draggingHub.id) {
      try {
        await onHubPositionUpdate(draggingHub.id, newPosition);
      } catch (err) {
        console.error('Failed to update hub position:', err);
      }
    }
    
    setDraggingHub(null);
  }, [draggingHub, onHubPositionUpdate]);

  // --- Hub Placement Handler ---
  const handleHubPlacement = useCallback((e) => {
    if (!hubPlacementMode || !onHubCreate) return;
    
    const svg = e.currentTarget.closest('svg');
    if (!svg) return;
    
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    const svgP = pt.matrixTransform(svg.getScreenCTM().inverse());
    
    const position = {
      x: Math.round(svgP.x),
      y: Math.round(svgP.y)
    };
    
    // Store position and show modal
    setPendingHubPosition(position);
    setShowHubModal(true);
    setHubPlacementMode(false); // Exit placement mode
  }, [hubPlacementMode, onHubCreate]);
  
  // --- Handle Hub Creation from Modal ---
  const handleCreateHubFromModal = useCallback(async (uwbAnchorId, macAddress) => {
    if (!pendingHubPosition || !onHubCreate) return;
    
    try {
      // Get warehouse name for automatic naming
      const warehouse = warehouses[currentWarehouseId];
      const warehouseName = warehouse?.name || warehouse?.display_name || 'Warehouse';
      
      // Count hubs in this warehouse for numbering
      const warehouseHubs = hubs.filter(h => h.warehouse_id === currentWarehouseId);
      const hubNumber = warehouseHubs.length + 1;
      
      // Generate automatic name: "Hub 1 (Main Warehouse)"
      const hubName = `Hub ${hubNumber} (${warehouseName})`;
      
      // Use UWB anchor ID as the hub_id (this is what the hardware will send)
      const hubId = uwbAnchorId;
      
      await onHubCreate({
        hubId: hubId, // UWB anchor ID (e.g., "A43F")
        name: hubName, // Display name
        warehouseId: currentWarehouseId,
        position: pendingHubPosition,
        uwbAnchorId: uwbAnchorId, // Store UWB anchor ID
        macAddress: macAddress // Store full MAC address
      });
      
      // Reset state
      setPendingHubPosition(null);
      setShowHubModal(false);
    } catch (err) {
      console.error('Failed to create hub:', err);
      alert(`Failed to create hub: ${err.message}`);
    }
  }, [pendingHubPosition, onHubCreate, hubs, currentWarehouseId, warehouses]);

  // Handle mouse move for dragging
  useEffect(() => {
    if (!draggingAsset && !draggingHub) return;
    
    const handleMouseMove = (e) => {
      if (draggingAsset) handleAssetDrag(e);
      if (draggingHub) handleHubDrag(e);
    };
    
    const handleMouseUp = () => {
      if (draggingAsset) handleAssetDragEnd();
      if (draggingHub) handleHubDragEnd();
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [draggingAsset, draggingHub, handleAssetDrag, handleAssetDragEnd, handleHubDrag, handleHubDragEnd]);

  // Zone color palette (matching Valley Head zones)
  const ZONE_COLORS = [
    { color: 'rgba(59, 130, 246, 0.25)', borderColor: 'rgb(59, 130, 246)' },   // Blue (Zone A)
    { color: 'rgba(34, 197, 94, 0.25)', borderColor: 'rgb(34, 197, 94)' },     // Green (Zone B)
    { color: 'rgba(234, 179, 8, 0.25)', borderColor: 'rgb(234, 179, 8)' },     // Yellow (Zone C)
    { color: 'rgba(168, 85, 247, 0.25)', borderColor: 'rgb(168, 85, 247)' },   // Purple (Zone D)
    { color: 'rgba(239, 68, 68, 0.25)', borderColor: 'rgb(239, 68, 68)' },     // Red (Zone E)
    { color: 'rgba(6, 182, 212, 0.25)', borderColor: 'rgb(6, 182, 212)' },     // Cyan (Zone F)
    { color: 'rgba(249, 115, 22, 0.25)', borderColor: 'rgb(249, 115, 22)' },   // Orange (Zone G)
    { color: 'rgba(236, 72, 153, 0.25)', borderColor: 'rgb(236, 72, 153)' },   // Pink (Zone H)
    { color: 'rgba(132, 204, 22, 0.25)', borderColor: 'rgb(132, 204, 22)' },   // Lime (Zone I)
    { color: 'rgba(99, 102, 241, 0.25)', borderColor: 'rgb(99, 102, 241)' },   // Indigo (Zone J)
  ];

  const handleAddZone = () => {
     // Get existing zone letters to find the next available letter
     const existingZones = data.zones || [];
     const zoneLetterPattern = /^Zone ([A-Z])$/;
     const usedLetters = new Set();
     
     existingZones.forEach(z => {
       const match = z.name?.match(zoneLetterPattern);
       if (match) {
         usedLetters.add(match[1]);
       }
     });
     
     // Find next available letter (A-Z)
     let nextLetter = 'A';
     for (let i = 0; i < 26; i++) {
       const letter = String.fromCharCode(65 + i); // A=65 in ASCII
       if (!usedLetters.has(letter)) {
         nextLetter = letter;
         break;
       }
     }
     
     // Get color based on letter index (A=0, B=1, etc.)
     const letterIndex = nextLetter.charCodeAt(0) - 65;
     const colorScheme = ZONE_COLORS[letterIndex % ZONE_COLORS.length];
     
     const newZone = {
         id: `zone-${Date.now()}`,
         name: `Zone ${nextLetter}`,
         displayName: `Zone ${nextLetter}`,
         color: colorScheme.color,
         borderColor: colorScheme.borderColor,
         rect: { x: 100, y: 100, width: 200, height: 120 },
         labelPos: { x: 0, y: 0 } // will be auto-calculated by fallback if 0
     };
     if (onZoneUpdate) onZoneUpdate([...existingZones, newZone]);
  };

  // Delete a zone
  const handleDeleteZone = (zoneId) => {
    const zone = data.zones.find(z => z.id === zoneId);
    const zoneName = zone?.name || 'this zone';
    
    // Check if zone has assets
    const assetsInZone = data.assets.filter(a => a.zoneId === zoneId);
    
    let confirmMessage = `Are you sure you want to delete "${zoneName}"?`;
    if (assetsInZone.length > 0) {
      confirmMessage += `\n\nWarning: This zone contains ${assetsInZone.length} asset(s). The assets will become unassigned.`;
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    // Save current state to history before deleting
    saveZonesToHistory(data.zones);
    
    // Remove the zone
    const updatedZones = data.zones.filter(z => z.id !== zoneId);
    if (onZoneUpdate) onZoneUpdate(updatedZones);
  };

  return (
    <div className={`h-full w-full relative ${isDark ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Build Mode Overlay */}
      {buildMode && (
        <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 z-50 border rounded-xl p-4 flex flex-col gap-3 shadow-2xl backdrop-blur-sm min-w-[360px] ${
          isDark 
            ? 'bg-gray-900/95 border-gray-600' 
            : 'bg-white/95 border-gray-300'
        }`}>
          {/* Mode Tabs */}
          <div className={`flex border-b mb-1 ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
            <button
              onClick={() => { setGpsBuildSubMode('create'); setBuildPoints([]); setEditingWarehouseId(null); }}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                gpsBuildSubMode === 'create' 
                  ? 'text-blue-500 border-b-2 border-blue-500 -mb-px' 
                  : isDark
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Create New
            </button>
            <button
              onClick={() => { setGpsBuildSubMode('edit'); setBuildPoints([]); setEditingWarehouseId(null); }}
              className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
                gpsBuildSubMode === 'edit' 
                  ? 'text-amber-500 border-b-2 border-amber-500 -mb-px' 
                  : isDark
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Edit Existing
            </button>
          </div>

          {/* Create Mode Content */}
          {gpsBuildSubMode === 'create' && (
            <>
              <div className={`font-medium text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>Create New Warehouse</div>
              
              {/* Organization Selector */}
              <div className="mb-2">
                <label className={`text-xs block mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Link to Organization (optional)</label>
                <select
                  value={selectedOrgId || ''}
                  onChange={(e) => setSelectedOrgId(e.target.value || null)}
                  className={`w-full px-3 py-2 border rounded text-sm focus:outline-none focus:border-blue-500 ${
                    isDark 
                      ? 'bg-gray-800 border-gray-600 text-gray-200' 
                      : 'bg-white border-gray-300 text-gray-900'
                  }`}
                >
                  <option value="">-- No Organization --</option>
                  {organizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.type})
                    </option>
                  ))}
                </select>
              </div>
              
              <div className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Click map to add corners • Click marker to select • Drag markers to move ({buildPoints.length} points)</div>
              
              {/* Editing Tools */}
              <div className={`flex gap-1 justify-center items-center border-t pt-2 mt-1 ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                <button
                  onClick={handleBuildPointsUndo}
                  disabled={!canUndoBuildPoints}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    canUndoBuildPoints 
                      ? isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      : isDark
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Undo"
                >
                  ↶ Undo
                </button>
                <button
                  onClick={handleBuildPointsRedo}
                  disabled={!canRedoBuildPoints}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    canRedoBuildPoints 
                      ? isDark
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      : isDark
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Redo"
                >
                  ↷ Redo
                </button>
                <div className={`w-px h-4 mx-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                <button
                  onClick={() => selectedPointIndex !== null && handleDeleteBuildPoint(selectedPointIndex)}
                  disabled={selectedPointIndex === null || buildPoints.length <= 3}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedPointIndex !== null && buildPoints.length > 3 
                      ? isDark
                        ? 'bg-red-900/60 hover:bg-red-900/80 text-red-300'
                        : 'bg-red-100 hover:bg-red-200 text-red-700'
                      : isDark
                        ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  }`}
                  title="Delete selected point"
                >
                  🗑 Delete Point
                </button>
              </div>
              
              {selectedPointIndex !== null && (
                <div className={`text-xs text-center ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>Point {selectedPointIndex + 1} selected</div>
              )}
              
              <div className="flex gap-2 justify-center">
                <button 
                  onClick={handleFinishBuild}
                  className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded text-sm font-medium flex-1"
                  disabled={buildPoints.length < 3}
                >
                  Finish & Save
                </button>
                <button 
                  onClick={handleClearBuildPoints}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    isDark 
                      ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                  }`}
                >
                  Clear
                </button>
                <button 
                  onClick={() => { setBuildMode(false); handleClearBuildPoints(); setSelectedOrgId(null); }}
                  className={`px-4 py-2 rounded text-sm font-medium ${
                    isDark 
                      ? 'bg-red-900/60 hover:bg-red-900/80 text-red-200' 
                      : 'bg-red-100 hover:bg-red-200 text-red-700'
                  }`}
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {/* Edit Mode Content */}
          {gpsBuildSubMode === 'edit' && (
            <>
              <div className={`font-medium text-center ${isDark ? 'text-white' : 'text-gray-900'}`}>Edit GPS Coordinates</div>
              
              {!editingWarehouseId ? (
                <>
                  <div className={`text-xs text-center mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Select a warehouse to edit its GPS polygon</div>
                  
                  {warehousesWithGPS.length === 0 ? (
                    <div className={`text-xs text-center italic py-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No warehouses with GPS coordinates found</div>
                  ) : (
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {warehousesWithGPS.map(wh => (
                        <button
                          key={wh.id}
                          onClick={() => handleSelectWarehouseForGPSEdit(wh.id)}
                          className={`w-full px-3 py-2 text-left text-sm rounded transition-colors ${
                            isDark 
                              ? 'bg-gray-800 hover:bg-gray-700 text-gray-200' 
                              : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                          }`}
                        >
                          <div className="font-medium">{wh.display_name || wh.name}</div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{wh.gps?.polygon?.length || 0} points</div>
                        </button>
                      ))}
                    </div>
                  )}
                  
                  <button 
                    onClick={() => { setBuildMode(false); setBuildPoints([]); }}
                    className={`px-4 py-2 rounded text-sm font-medium w-full ${
                      isDark 
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <div className={`text-xs text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Editing: <span className={`font-medium ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{warehouses[editingWarehouseId]?.display_name || warehouses[editingWarehouseId]?.name}</span>
                  </div>
                  <div className={`text-xs text-center ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Click marker to select • Drag markers to move ({buildPoints.length} points)</div>
                  <div className={`text-xs text-center italic ${isDark ? 'text-amber-500/70' : 'text-amber-600/80'}`}>Note: This only changes GPS coordinates, not the blueprint</div>
                  
                  {/* Editing Tools */}
                  <div className={`flex gap-1 justify-center items-center border-t pt-2 mt-1 ${isDark ? 'border-gray-700' : 'border-gray-300'}`}>
                    <button
                      onClick={handleBuildPointsUndo}
                      disabled={!canUndoBuildPoints}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        canUndoBuildPoints 
                          ? isDark
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          : isDark
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      title="Undo"
                    >
                      ↶ Undo
                    </button>
                    <button
                      onClick={handleBuildPointsRedo}
                      disabled={!canRedoBuildPoints}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        canRedoBuildPoints 
                          ? isDark
                            ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          : isDark
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      title="Redo"
                    >
                      ↷ Redo
                    </button>
                    <div className={`w-px h-4 mx-1 ${isDark ? 'bg-gray-600' : 'bg-gray-300'}`}></div>
                    <button
                      onClick={() => selectedPointIndex !== null && handleDeleteBuildPoint(selectedPointIndex)}
                      disabled={selectedPointIndex === null || buildPoints.length <= 3}
                      className={`px-2 py-1 rounded text-xs font-medium ${
                        selectedPointIndex !== null && buildPoints.length > 3 
                          ? isDark
                            ? 'bg-red-900/60 hover:bg-red-900/80 text-red-300'
                            : 'bg-red-100 hover:bg-red-200 text-red-700'
                          : isDark
                            ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      }`}
                      title="Delete selected point"
                    >
                      🗑 Delete Point
                    </button>
                  </div>
                  
                  {selectedPointIndex !== null && (
                    <div className={`text-xs text-center ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>Point {selectedPointIndex + 1} selected</div>
                  )}
                  
                  <div className="flex gap-2 justify-center">
                    <button 
                      onClick={handleSaveGPSEdit}
                      className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded text-sm font-medium flex-1"
                      disabled={buildPoints.length < 3}
                    >
                      Save Changes
                    </button>
                    <button 
                      onClick={() => handleSelectWarehouseForGPSEdit(editingWarehouseId)}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        isDark 
                          ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      Reset
                    </button>
                    <button 
                      onClick={handleCancelGPSEdit}
                      className={`px-4 py-2 rounded text-sm font-medium ${
                        isDark 
                          ? 'bg-red-900/60 hover:bg-red-900/80 text-red-200' 
                          : 'bg-red-100 hover:bg-red-200 text-red-700'
                      }`}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Controls - Reorganized and Compact */}
      <div className="absolute top-3 left-3 z-20 flex flex-wrap gap-1.5 items-center">
        {/* Group 1: Warehouse Selector */}
        {!useGoogleView && onWarehouseChange && (() => {
          const customerWarehouses = Object.entries(warehouses).filter(([_, wh]) => wh.org_type === 'Customer');
          if (customerWarehouses.length === 0) return null;
          
          return (
            <div className="relative">
              <select
                value={currentWarehouseId || customerWarehouses[0]?.[0]}
                onChange={(e) => onWarehouseChange(e.target.value)}
                className={`pl-2 pr-7 py-1.5 rounded text-sm border focus:outline-none appearance-none cursor-pointer font-medium shadow-lg ${
                  isDark 
                    ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700' 
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {customerWarehouses.map(([id, warehouse]) => (
                  <option key={id} value={id}>
                    {warehouse.display_name || warehouse.name || id}
                  </option>
                ))}
              </select>
              <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          );
        })()}

        {/* Group 2: View Mode Buttons */}
        <div className={`flex items-center gap-1 rounded-md ${isDark ? 'bg-gray-800/50' : 'bg-gray-100/50'} p-0.5`}>
          <button
            onClick={() => { setUseGoogleView(false); resetToFull(); if (onViewChange) onViewChange(false, []); }}
            className={`px-3 py-1.5 rounded text-sm border ${
              !useGoogleView 
                ? isDark
                  ? 'bg-gray-700 text-white border-gray-600'
                  : 'bg-blue-600 text-white border-blue-500'
                : isDark
                  ? 'bg-transparent text-gray-300 border-transparent hover:bg-gray-700'
                  : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-200'
            }`}
            title="Warehouse View"
          >
            Warehouse
          </button>
          {!useGoogleView && (
            <button
              onClick={() => { setUseGoogleView(true); setBuildMode(false); if (onViewChange) onViewChange(true, externalAssets); }}
              className={`px-3 py-1.5 rounded text-sm border ${
                isDark 
                  ? 'bg-transparent text-gray-300 border-transparent hover:bg-gray-700' 
                  : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-200'
              }`}
              title="Map View"
            >
              Map
            </button>
          )}
          {useGoogleView && (
            <button
              onClick={() => setMapType(t => t === 'roadmap' ? 'hybrid' : 'roadmap')}
              className={`px-3 py-1.5 rounded text-sm border ${
                mapType === 'hybrid' 
                  ? isDark
                    ? 'bg-green-700 text-white border-green-600'
                    : 'bg-green-600 text-white border-green-500'
                  : isDark
                    ? 'bg-transparent text-gray-300 border-transparent hover:bg-gray-700'
                    : 'bg-transparent text-gray-600 border-transparent hover:bg-gray-200'
              }`}
              title="Toggle Satellite/Map"
            >
              {mapType === 'hybrid' ? 'Map' : 'Sat'}
            </button>
          )}
        </div>

        {/* Group 3: Filter Dropdown - Warehouse View Only */}
        {!useGoogleView && (
          <div className="relative" ref={filterDropdownRef}>
            <button
              onClick={() => setFilterDropdownOpen(!filterDropdownOpen)}
              className={`px-3 py-1.5 rounded text-sm border ${
                filterDropdownOpen
                  ? isDark
                    ? 'bg-gray-700 text-white border-gray-600'
                    : 'bg-blue-600 text-white border-blue-500'
                  : isDark
                    ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
              title="Filter visibility"
            >
              Filter
            </button>
            
            {/* Filter Dropdown Menu */}
            {filterDropdownOpen && (
              <div 
                className={`absolute top-full left-0 mt-1 rounded-md shadow-lg border z-50 min-w-[140px] ${
                  isDark 
                    ? 'bg-gray-800 border-gray-700' 
                    : 'bg-white border-gray-200'
                }`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="p-1.5 space-y-1">
                  {/* Zones Toggle */}
                  <button
                    onClick={() => setShowZones(!showZones)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${
                      showZones
                        ? isDark
                          ? 'bg-purple-700/30 text-purple-300 border border-purple-600/50'
                          : 'bg-purple-50 text-purple-700 border border-purple-200'
                        : isDark
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>Zones</span>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      showZones
                        ? isDark
                          ? 'bg-purple-600 border-purple-500'
                          : 'bg-purple-600 border-purple-500'
                        : isDark
                          ? 'bg-transparent border-gray-600'
                          : 'bg-transparent border-gray-300'
                    }`}>
                      {showZones && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                  
                  {/* Assets Toggle */}
                  <button
                    onClick={() => setShowAssets(!showAssets)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${
                      showAssets
                        ? isDark
                          ? 'bg-blue-700/30 text-blue-300 border border-blue-600/50'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                        : isDark
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>Assets</span>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      showAssets
                        ? isDark
                          ? 'bg-blue-600 border-blue-500'
                          : 'bg-blue-600 border-blue-500'
                        : isDark
                          ? 'bg-transparent border-gray-600'
                          : 'bg-transparent border-gray-300'
                    }`}>
                      {showAssets && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                  
                  {/* Hubs Toggle */}
                  <button
                    onClick={() => setShowHubs(!showHubs)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${
                      showHubs
                        ? isDark
                          ? 'bg-cyan-700/30 text-cyan-300 border border-cyan-600/50'
                          : 'bg-cyan-50 text-cyan-700 border border-cyan-200'
                        : isDark
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>Hubs</span>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      showHubs
                        ? isDark
                          ? 'bg-cyan-600 border-cyan-500'
                          : 'bg-cyan-600 border-cyan-500'
                        : isDark
                          ? 'bg-transparent border-gray-600'
                          : 'bg-transparent border-gray-300'
                    }`}>
                      {showHubs && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                  
                  {/* Metrics Toggle */}
                  <button
                    onClick={() => setShowUWBDistances(!showUWBDistances)}
                    className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs transition-colors ${
                      showUWBDistances
                        ? isDark
                          ? 'bg-green-700/30 text-green-300 border border-green-600/50'
                          : 'bg-green-50 text-green-700 border border-green-200'
                        : isDark
                          ? 'text-gray-300 hover:bg-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span>Metrics</span>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                      showUWBDistances
                        ? isDark
                          ? 'bg-green-600 border-green-500'
                          : 'bg-green-600 border-green-500'
                        : isDark
                          ? 'bg-transparent border-gray-600'
                          : 'bg-transparent border-gray-300'
                    }`}>
                      {showUWBDistances && (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Group 4: Build Mode Controls */}
        {useGoogleView && (
          <button
            onClick={() => { setBuildMode(!buildMode); setBuildPoints([]); }}
            className={`px-3 py-1.5 rounded text-sm border ${
              buildMode 
                ? 'bg-blue-600 text-white border-blue-500' 
                : isDark
                  ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            title="Build Mode"
          >
            {buildMode ? 'Exit' : 'Build'}
          </button>
        )}
        
        {!useGoogleView && activeView.type !== 'default' && (
          <button
            onClick={resetToFull}
            className={`px-3 py-1.5 rounded text-sm border ${
              isDark 
                ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700' 
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            title="Reset View"
          >
            Reset
          </button>
        )}

        {/* Build Mode Controls - Warehouse View Only */}
        {!useGoogleView && activeView.type === 'default' && (onZoneUpdate || onBlueprintUpdate) && (
           <>
             {/* Build Mode Dropdown */}
             <div className="relative">
               <button 
                 onClick={() => {
                   if (editMode || blueprintEditMode) {
                     // Exit all edit modes
                     setEditMode(false);
                     setBlueprintEditMode(false);
                     setAddCornerMode(false);
                     setContextMenu(null);
                     setBuildModeDropdownOpen(false);
                   } else {
                     setBuildModeDropdownOpen(!buildModeDropdownOpen);
                   }
                 }}
                 className={`px-3 py-1.5 rounded text-sm border ${
                   editMode || blueprintEditMode 
                     ? 'bg-blue-600 text-white border-blue-500' 
                     : isDark
                       ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                       : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                 }`}
                 title="Build Mode"
               >
                 {editMode || blueprintEditMode ? 'Exit' : 'Build'}
               </button>
               
               {/* Dropdown Menu */}
               {buildModeDropdownOpen && !editMode && !blueprintEditMode && (
                 <div className={`absolute top-full left-0 mt-1 border rounded-md shadow-lg overflow-hidden z-30 min-w-[140px] ${
                   isDark 
                     ? 'bg-gray-800 border-gray-700' 
                     : 'bg-white border-gray-200'
                 }`}>
                   {onZoneUpdate && (
                     <button
                       onClick={() => {
                         setEditMode(true);
                         setBlueprintEditMode(false);
                         setBuildModeDropdownOpen(false);
                       }}
                       className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                         isDark 
                           ? 'text-gray-100 hover:bg-gray-700' 
                           : 'text-gray-700 hover:bg-gray-100'
                       }`}
                     >
                       <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                       Edit Zones
                     </button>
                   )}
                  {onBlueprintUpdate && (
                    <>
                      <button
                        onClick={() => {
                          setBlueprintEditMode(true);
                          setEditMode(false);
                          setAddCornerMode(false);
                          setContextMenu(null);
                          setBuildModeDropdownOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                          isDark 
                            ? 'text-gray-100 hover:bg-gray-700' 
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                       <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                       Edit Blueprint
                      </button>
                    </>
                  )}
                </div>
               )}
             </div>

             {/* UWB Positioning Method Toggle */}
             <div className="flex items-center gap-0.5 border rounded overflow-hidden text-xs"
               style={{ borderColor: isDark ? '#374151' : '#d1d5db' }}
               title="Switch UWB positioning algorithm"
             >
               <button
                 onClick={() => setPositioningMethod('pairwise')}
                 className={`px-2.5 py-1.5 transition-colors font-medium ${
                   positioningMethod === 'pairwise'
                     ? 'bg-orange-500 text-white'
                     : isDark
                       ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                       : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                 }`}
                 title="Pairwise Averaging — 3-pair bilateration average"
               >
                 Pairwise
               </button>
               <button
                 onClick={() => setPositioningMethod('pairwise2')}
                 className={`px-2.5 py-1.5 transition-colors font-medium ${
                   positioningMethod === 'pairwise2'
                     ? 'bg-orange-600 text-white'
                     : isDark
                       ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                       : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                 }`}
                 title="Pairwise #2 — Refined with slant correction, distance capping, weighted averaging"
               >
                 Pairwise #2
               </button>
               <button
                 onClick={() => setPositioningMethod('linear')}
                 className={`px-2.5 py-1.5 transition-colors font-medium ${
                   positioningMethod === 'linear'
                     ? 'bg-emerald-500 text-white'
                     : isDark
                       ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                       : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                 }`}
                 title="Linear + Z-Gap — algebraic trilateration with height correction"
               >
                 Linear
               </button>
               <button
                 onClick={() => setPositioningMethod('linear2')}
                 className={`px-2.5 py-1.5 transition-colors font-medium ${
                   positioningMethod === 'linear2'
                     ? 'bg-emerald-600 text-white'
                     : isDark
                       ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                       : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                 }`}
                 title="Linear #2 — Refined with velocity filtering and smoothing"
               >
                 Linear #2
               </button>
               <button
                 onClick={() => setPositioningMethod('all')}
                 className={`px-2.5 py-1.5 transition-colors font-medium ${
                   positioningMethod === 'all'
                     ? 'bg-rose-600 text-white'
                     : isDark
                       ? 'bg-gray-800 text-gray-400 hover:bg-gray-700 hover:text-gray-200'
                       : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                 }`}
                 title="ALL — run pairwise and linear methods simultaneously and show each as a separate dot on the map"
               >
                 ALL
               </button>
             </div>

             {/* Active Method Label */}
             {!useGoogleView && positioningMethod && positioningMethod !== 'all' && (
               <div className={`px-3 py-1.5 rounded text-xs border font-medium ${
                 isDark
                   ? 'bg-gray-800 text-gray-100 border-gray-700'
                   : 'bg-white text-gray-700 border-gray-300'
               }`}>
                 Active: {
                   positioningMethod === 'pairwise' ? 'Pairwise' :
                   positioningMethod === 'pairwise2' ? 'Pairwise #2' :
                   positioningMethod === 'linear' ? 'Linear' :
                   positioningMethod === 'linear2' ? 'Linear #2' :
                   positioningMethod
                 }
               </div>
             )}

             {/* Calibrate Dropdown - NEW */}
             <div className="relative">
               <button 
                 onClick={() => setCalibrateModeDropdownOpen(!calibrateModeDropdownOpen)}
                 className={`px-3 py-1.5 rounded text-sm border ${
                   calibrateModeDropdownOpen
                     ? 'bg-green-600 text-white border-green-500' 
                     : isDark
                       ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                       : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                 }`}
                 title="Calibration Options"
               >
                 📏 Calibrate
               </button>
               
               {/* Calibrate Dropdown Menu */}
               {calibrateModeDropdownOpen && (
                 <div className={`absolute top-full left-0 mt-1 border rounded-md shadow-lg overflow-hidden z-30 min-w-[180px] ${
                   isDark 
                     ? 'bg-gray-800 border-gray-700' 
                     : 'bg-white border-gray-200'
                 }`}>
                   <button
                     onClick={() => {
                       setShowBlueprintEditor(true);
                       setCalibrateModeDropdownOpen(false);
                     }}
                     className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                       isDark 
                         ? 'text-gray-100 hover:bg-gray-700' 
                         : 'text-gray-700 hover:bg-gray-100'
                     }`}
                   >
                     <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                     Manual Scale
                   </button>
                   <button
                     onClick={() => {
                       setShowUWBCalibration(true);
                       setCalibrateModeDropdownOpen(false);
                     }}
                     className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                       isDark 
                         ? 'text-gray-100 hover:bg-gray-700' 
                         : 'text-gray-700 hover:bg-gray-100'
                     }`}
                   >
                     <span className="w-2 h-2 rounded-full bg-green-500"></span>
                     🎯 UWB Auto-Calibrate
                   </button>
                 </div>
               )}
             </div>
           </>
        )}

            
        {/* Zone Edit Mode Tools */}
        {editMode && (
               <>
                 <button
                   onClick={handleAddZone}
                   className="px-3 py-1.5 rounded-md text-sm bg-amber-600 text-white border border-amber-500 hover:bg-amber-500"
                 >
                   + Add Zone
                 </button>
                <button
                  onClick={() => setHubPlacementMode(!hubPlacementMode)}
                  className={`px-3 py-1.5 rounded-md text-sm border ${hubPlacementMode ? 'bg-cyan-600 text-white border-cyan-500' : 'bg-cyan-700 text-white border-cyan-600 hover:bg-cyan-600'}`}
                  title="Place ESP32 hub/gateway for UWB tracking"
                >
                  {hubPlacementMode ? '✓ Placing Hub...' : '📡 Add Hub'}
                </button>
                <button
                  onClick={() => setShowTagManager(true)}
                  className="px-3 py-1.5 rounded-md text-sm border bg-blue-700 text-white border-blue-600 hover:bg-blue-600"
                  title="Manage UWB tags (forklifts)"
                >
                  🚜 Manage Tags
                </button>
                 {onClearTestBeacons && trackedBeacons.some(b => b.bleTagId?.startsWith('TEST-BEACON-')) && (
                   <button
                     onClick={onClearTestBeacons}
                     className="px-3 py-1.5 rounded-md text-sm border bg-red-700 text-white border-red-600 hover:bg-red-600"
                     title="Clear all test beacons"
                   >
                     🧹 Clear Tests
                   </button>
                 )}
                 <button
                   onClick={handleUndo}
                   disabled={!canUndo}
                   className={`px-3 py-1.5 rounded-md text-sm border ${
                     canUndo 
                       ? isDark
                         ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                         : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                       : isDark
                         ? 'bg-gray-900 text-gray-500 border-gray-800 cursor-not-allowed'
                         : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                   }`}
                   title="Undo"
                 >
                   ↶ Undo
                 </button>
                 <button
                   onClick={handleRedo}
                   disabled={!canRedo}
                   className={`px-3 py-1.5 rounded-md text-sm border ${
                     canRedo 
                       ? isDark
                         ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                         : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                       : isDark
                         ? 'bg-gray-900 text-gray-500 border-gray-800 cursor-not-allowed'
                         : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                   }`}
                   title="Redo"
                 >
                   ↷ Redo
                 </button>
               </>
        )}
             
        {/* Blueprint Edit Mode Tools */}
        {blueprintEditMode && (
               <>
                 <button
                   onClick={() => setAddCornerMode(!addCornerMode)}
                   className={`px-3 py-1.5 rounded-md text-sm border ${addCornerMode ? 'bg-red-600 text-white border-red-500' : 'bg-purple-600 text-white border-purple-500 hover:bg-purple-500'}`}
                 >
                   {addCornerMode ? 'Cancel Add' : '+ Add Corner'}
                 </button>
                 <button
                   onClick={handleUndo}
                   disabled={!canUndo}
                   className={`px-3 py-1.5 rounded-md text-sm border ${
                     canUndo 
                       ? isDark
                         ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                         : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                       : isDark
                         ? 'bg-gray-900 text-gray-500 border-gray-800 cursor-not-allowed'
                         : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                   }`}
                   title="Undo"
                 >
                   ↶ Undo
                 </button>
                 <button
                   onClick={handleRedo}
                   disabled={!canRedo}
                   className={`px-3 py-1.5 rounded-md text-sm border ${
                     canRedo 
                       ? isDark
                         ? 'bg-gray-800 text-gray-100 border-gray-700 hover:bg-gray-700'
                         : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                       : isDark
                         ? 'bg-gray-900 text-gray-500 border-gray-800 cursor-not-allowed'
                         : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                   }`}
                   title="Redo"
                 >
                   ↷ Redo
                 </button>
                 <button
                   onClick={() => setShowBlueprintEditor(true)}
                   className="px-3 py-1.5 rounded-md text-sm bg-purple-600 text-white border border-purple-500 hover:bg-purple-500"
                 >
                   Upload
                 </button>
               </>
        )}
      </div>

      {/* Warehouse SVG view */}
      <motion.div
        ref={mapContainerRef}
        className="absolute inset-0 z-10"
        initial={{ opacity: 1, scale: 1 }}
        animate={{ opacity: useGoogleView ? 0 : 1, scale: useGoogleView ? 0.98 : 1 }}
        transition={{ duration: 0.35, ease: 'easeInOut' }}
        style={{ pointerEvents: useGoogleView ? 'none' : 'auto' }}
        onMouseDown={handlePanStart}
      >
        <motion.svg
          ref={svgRef}
          className="h-full w-full min-h-full min-w-full"
          viewBox={viewBox}
          xmlns="http://www.w3.org/2000/svg"
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onClick={(e) => {
            console.log('SVG onClick triggered', { hubPlacementMode, blueprintEditMode, addCornerMode, target: e.target.tagName, currentTarget: e.currentTarget.tagName });
            
            // Hub placement mode
            if (hubPlacementMode) {
              handleHubPlacement(e);
              return;
            }
            // Blueprint corner mode
            if (blueprintEditMode && addCornerMode) {
              handleAddCornerToBlueprint(e);
              return;
            }
          }}
          style={{ cursor: hubPlacementMode ? 'crosshair' : ((editMode || blueprintEditMode) ? (dragState ? 'grabbing' : (addCornerMode ? 'crosshair' : 'grab')) : 'grab') }}
        >
        {/* Background grid covering the default view bounds */}
        {/* Click background to reset to full view - only if zoomed in */}
        {(() => {
          const [vx, vy, vw, vh] = defaultViewBox.split(' ').map(Number);
          const isZoomedIn = activeView.type !== 'default' || selectedZoneId || selectedAssetId || selectedRackId;
          return (
            <rect
              className="pan-target" 
              x={vx} 
              y={vy} 
              width={vw} 
              height={vh} 
              fill={isDark ? "#0f172a" : "#f8fafc"} 
              onClick={(e) => {
                // Only reset if zoomed in and not in edit mode
                if (isZoomedIn && !editMode && !blueprintEditMode && !hubPlacementMode) {
                  e.stopPropagation();
                  resetToFull();
                }
              }}
              style={{ cursor: isZoomedIn && !editMode && !blueprintEditMode && !hubPlacementMode ? 'pointer' : 'default' }}
            />
          );
        })()}

        {/* Warehouse outline behind elements (non-rectangular, buffered) */}
        {outlinePath && (
          <g>
            <path d={outlinePath} fill="rgba(59,130,246,0.06)" stroke="#60a5fa" strokeWidth={3} strokeLinejoin="miter" />
            {/* Warehouse outline handles (Blueprint Edit Mode) */}
            {blueprintEditMode && data.blueprint?.outlinePoints?.map((pt, idx) => {
              // Ensure exact integer positioning - no floating point drift
              const exactX = Math.round(pt.x);
              const exactY = Math.round(pt.y);
              
              return (
                <g key={`handle-${idx}`}>
                  {/* Anchor point - exact corner position */}
                  <circle
                    cx={exactX}
                    cy={exactY}
                    r={addCornerMode ? 8 : 6}
                    fill={addCornerMode ? "#ef4444" : "#3b82f6"}
                    stroke="#ffffff"
                    strokeWidth={2}
                    className="cursor-pointer hover:r-10 transition-all"
                    style={{ cursor: 'move', pointerEvents: 'all' }}
                    onMouseDown={(e) => handleVertexMouseDown(e, idx, { x: exactX, y: exactY })}
                    onContextMenu={(e) => handleVertexContextMenu(e, idx)}
                  />
                  {/* Visual indicator that this is an anchor point */}
                  <circle
                    cx={exactX}
                    cy={exactY}
                    r={2}
                    fill="#ffffff"
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              );
            })}
            
            {/* Show outline path as clickable when in add corner mode */}
            {blueprintEditMode && addCornerMode && outlinePath && (
              <path 
                d={outlinePath} 
                fill="transparent" 
                stroke="rgba(239, 68, 68, 0.5)" 
                strokeWidth={10}
                strokeLinecap="round"
                style={{ cursor: 'crosshair', pointerEvents: 'stroke' }}
                onClick={handleAddCornerToBlueprint}
              />
            )}
          </g>
        )}

        {/* Blueprint static elements */}
        {data.blueprint?.hallways?.map(h => (
          <rect key={h.id} x={h.x} y={h.y} width={h.width} height={h.height} fill={isDark ? "#0b1220" : "#e2e8f0"} stroke={isDark ? "#1f2937" : "#cbd5e1"} strokeWidth={1} />
        ))}
        {data.blueprint?.walls?.map(w => (
          <line key={w.id} x1={w.x1} y1={w.y1} x2={w.x2} y2={w.y2} stroke={isDark ? "#7aa7f9" : "#3b82f6"} strokeWidth={2} />
        ))}
        {data.blueprint?.offices?.map(o => (
          <g key={o.id}>
            <rect x={o.x} y={o.y} width={o.width} height={o.height} rx={6} ry={6} fill={isDark ? "#0b1220" : "#e2e8f0"} stroke={isDark ? "#64748b" : "#94a3b8"} strokeWidth={1} />
            <text x={o.x + o.width/2} y={o.y + o.height/2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fill={isDark ? "#94a3b8" : "#475569"}>Office</text>
          </g>
        ))}
        {data.blueprint?.bathrooms?.map(b => (
          <g key={b.id}>
            <rect x={b.x} y={b.y} width={b.width} height={b.height} rx={6} ry={6} fill={isDark ? "#0b1220" : "#e2e8f0"} stroke={isDark ? "#94a3b8" : "#cbd5e1"} strokeWidth={1} />
            <text x={b.x + b.width/2} y={b.y + b.height/2} textAnchor="middle" dominantBaseline="middle" fontSize={9} fill={isDark ? "#cbd5e1" : "#64748b"}>WC</text>
          </g>
        ))}
        {data.blueprint?.conferenceRooms?.map(c => (
          <g key={c.id}>
            <rect x={c.x} y={c.y} width={c.width} height={c.height} rx={8} ry={8} fill={isDark ? "#0b1220" : "#e2e8f0"} stroke={isDark ? "#93c5fd" : "#3b82f6"} strokeWidth={1} />
            <text x={c.x + c.width/2} y={c.y + c.height/2} textAnchor="middle" dominantBaseline="middle" fontSize={10} fill={isDark ? "#bfdbfe" : "#1e40af"}>Conference</text>
          </g>
        ))}

        {/* Zones and Rows */}
        {showZones && mainViewZones.map((zone) => {
          const zoneOpacity = getZoneOpacity(zone.id);
          const isHovered = hoveredZoneId === zone.id;
          const isSelectedZone = zone.id === selectedAssetZoneId;
          
          // Use dragging position if this zone is being dragged
          const isDragging = draggingZonePosition?.id === zone.id;
          const displayRect = isDragging ? {
            x: draggingZonePosition.x,
            y: draggingZonePosition.y,
            width: draggingZonePosition.width,
            height: draggingZonePosition.height
          } : zone.rect;
          
          return (
            <g
              key={zone.id}
              onClick={(e) => { 
                e.stopPropagation();
                if (!editMode && !blueprintEditMode) { 
                  setActiveView({ type: 'zone', id: zone.id }); 
                  if (onZoneSelect) onZoneSelect(zone.id); 
                } 
              }}
              onMouseDown={(e) => handleZoneMouseDown(e, zone)}
              onMouseEnter={() => setHoveredZoneId(zone.id)}
              onMouseLeave={() => setHoveredZoneId((id) => (id === zone.id ? null : id))}
              className="cursor-pointer"
              transform={`translate(${zone.offset?.dx || 0}, ${zone.offset?.dy || 0})`}
            >
              {displayRect ? (
                <>
                  <motion.rect
                    x={displayRect.x}
                    y={displayRect.y}
                    width={displayRect.width}
                    height={displayRect.height}
                    rx={10}
                    ry={10}
                    fill={zone.color}
                    stroke={editMode ? '#3b82f6' : zone.borderColor}
                    initial={{ strokeWidth: 2, opacity: zoneOpacity }}
                    animate={{
                      strokeWidth: editMode ? 2 : (isHovered ? 3 : (isSelectedZone ? 3 : 2)),
                      opacity: isHovered ? 0.9 : zoneOpacity,
                    }}
                    transition={isDragging ? { duration: 0 } : { type: 'spring', stiffness: 200, damping: 20 }}
                    style={{ cursor: editMode ? 'move' : 'pointer' }}
                  />
                  
                  {/* Resize handles - only show in edit mode */}
                  {editMode && (
                    <>
                      {/* Corner handles */}
                      {[
                        { handle: 'nw', x: displayRect.x, y: displayRect.y, cursor: 'nw-resize' },
                        { handle: 'ne', x: displayRect.x + displayRect.width, y: displayRect.y, cursor: 'ne-resize' },
                        { handle: 'sw', x: displayRect.x, y: displayRect.y + displayRect.height, cursor: 'sw-resize' },
                        { handle: 'se', x: displayRect.x + displayRect.width, y: displayRect.y + displayRect.height, cursor: 'se-resize' },
                      ].map(({ handle, x, y, cursor }) => (
                        <rect
                          key={handle}
                          x={x - 5}
                          y={y - 5}
                          width={10}
                          height={10}
                          fill="#3b82f6"
                          stroke="#ffffff"
                          strokeWidth={1}
                          style={{ cursor }}
                          onMouseDown={(e) => handleZoneResizeMouseDown(e, zone, handle)}
                        />
                      ))}
                      
                      {/* Edge handles */}
                      {[
                        { handle: 'n', x: displayRect.x + displayRect.width/2, y: displayRect.y, cursor: 'n-resize' },
                        { handle: 's', x: displayRect.x + displayRect.width/2, y: displayRect.y + displayRect.height, cursor: 's-resize' },
                        { handle: 'w', x: displayRect.x, y: displayRect.y + displayRect.height/2, cursor: 'w-resize' },
                        { handle: 'e', x: displayRect.x + displayRect.width, y: displayRect.y + displayRect.height/2, cursor: 'e-resize' },
                      ].map(({ handle, x, y, cursor }) => (
                        <rect
                          key={handle}
                          x={x - 4}
                          y={y - 4}
                          width={8}
                          height={8}
                          fill="#60a5fa"
                          stroke="#ffffff"
                          strokeWidth={1}
                          rx={2}
                          style={{ cursor }}
                          onMouseDown={(e) => handleZoneResizeMouseDown(e, zone, handle)}
                        />
                      ))}
                      
                      {/* Zone name label in edit mode */}
                      <text
                        x={displayRect.x + displayRect.width/2}
                        y={displayRect.y - 10}
                        textAnchor="middle"
                        fontSize={10}
                        fill="#3b82f6"
                        fontWeight="500"
                      >
                        {zone.name}
                      </text>
                      
                      {/* Delete button - appears on hover in edit mode */}
                      <g
                        className="zone-delete-button"
                        style={{ opacity: isHovered ? 1 : 0, transition: 'opacity 0.2s' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteZone(zone.id);
                        }}
                      >
                        <circle
                          cx={displayRect.x + displayRect.width - 15}
                          cy={displayRect.y + 15}
                          r={12}
                          fill="#ef4444"
                          stroke="#ffffff"
                          strokeWidth={2}
                          style={{ cursor: 'pointer' }}
                        />
                        <text
                          x={displayRect.x + displayRect.width - 15}
                          y={displayRect.y + 15}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fontSize={14}
                          fill="#ffffff"
                          fontWeight="bold"
                          style={{ pointerEvents: 'none' }}
                        >
                          ×
                        </text>
                      </g>
                    </>
                  )}
                </>
              ) : (
                <motion.path
                  d={zone.path}
                  fill={zone.color}
                  stroke={zone.borderColor}
                  initial={{ strokeWidth: 2, opacity: zoneOpacity }}
                  animate={{
                    strokeWidth: isHovered ? 3 : (isSelectedZone ? 3 : 2),
                    opacity: isHovered ? 0.9 : zoneOpacity,
                  }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                />
              )}
              {/* Zone name that fades in/out on hover (only when not in edit mode) */}
              {!editMode && (
                <motion.text
                  x={displayRect ? displayRect.x + displayRect.width/2 : zone.labelPos?.x || 0}
                  y={displayRect ? displayRect.y + displayRect.height/2 : zone.labelPos?.y || 0}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={12}
                  fontWeight="500"
                  fill={isDark ? "#e2e8f0" : "#1e293b"}
                  stroke={isDark ? "#1e293b" : "#ffffff"}
                  strokeWidth={1}
                  paintOrder="stroke"
                  initial={{ opacity: 0 }}
                  animate={{
                    opacity: isHovered && activeView.type === 'default' ? 1 : 0,
                  }}
                  transition={{ duration: 0.2, ease: "easeInOut" }}
                  pointerEvents="none"
                >
                  {zone.name}
                </motion.text>
              )}
            </g>
          );
        })}


        {/* Historical Trace Path (Real UWB Data) */}
        {traceMode?.isActive && getTracePath && (
          <g>
            {/* Full trace path with confidence coloring */}
            {traceMode.trace.length >= 2 && traceMode.trace.map((point, i) => {
              if (i === traceMode.trace.length - 1) return null;
              
              const next = traceMode.trace[i + 1];
              const avgConfidence = (point.confidence + next.confidence) / 2;
              
              // Color based on confidence: blue (high) -> cyan (medium) -> orange (low)
              const getColor = (conf) => {
                if (conf >= 0.7) return '#3b82f6'; // blue
                if (conf >= 0.4) return '#06b6d4'; // cyan
                return '#f97316'; // orange
              };
              
              const color = getColor(avgConfidence);
              const opacity = point.interpolated ? 0.3 : 0.5;
              
              return (
                <line
                  key={i}
                  x1={point.position.x}
                  y1={point.position.y}
                  x2={next.position.x}
                  y2={next.position.y}
                  stroke={color}
                  strokeWidth={2}
                  strokeOpacity={opacity}
                  strokeDasharray={point.interpolated ? "2 2" : "none"}
                />
              );
            })}
            
            {/* Progress path (highlighted) */}
            <path
              d={getTracePath}
              fill="none"
              stroke={isDark ? "#3b82f6" : "#2563eb"}
              strokeWidth={4}
              strokeOpacity={0.9}
            />
            
            {/* Current position marker with icon */}
            {getTracePosition && (() => {
              // Determine if this is a drone or forklift
              const traceTag = uwbTags.find(t => t.tag_id === traceMode.tagId);
              const isDrone = traceTag?.tag_hardware_id === 'TAG001' || traceTag?.tag_name?.toLowerCase().includes('drone');
              const tagColor = '#3b82f6';
              
              return (
                <g>
                  {/* Pulsing ring */}
                  <motion.circle
                    cx={getTracePosition.x}
                    cy={getTracePosition.y}
                    r={10}
                    fill="none"
                    stroke={tagColor}
                    strokeWidth={2.5}
                    initial={{ r: 10, opacity: 0.8 }}
                    animate={{ r: 25, opacity: 0 }}
                    transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
                  />
                  
                  {/* Icon - Drone or Forklift */}
                  {isDrone ? (
                    <>
                      {/* Icon background circle - only for drone */}
                      <circle
                        cx={getTracePosition.x}
                        cy={getTracePosition.y}
                        r={14}
                        fill={tagColor}
                        stroke="#ffffff"
                        strokeWidth={3}
                      />
                      <g transform={`translate(${getTracePosition.x - 8}, ${getTracePosition.y - 8})`}>
                        {/* Drone icon */}
                        <circle cx="8" cy="8" r="3" fill="white" />
                        <rect x="6" y="7" width="4" height="2" fill="white" />
                        <circle cx="3" cy="3" r="2" fill="white" opacity="0.8" />
                        <circle cx="13" cy="3" r="2" fill="white" opacity="0.8" />
                        <circle cx="3" cy="13" r="2" fill="white" opacity="0.8" />
                        <circle cx="13" cy="13" r="2" fill="white" opacity="0.8" />
                        <line x1="5" y1="5" x2="8" y2="8" stroke="white" strokeWidth="1" />
                        <line x1="11" y1="5" x2="8" y2="8" stroke="white" strokeWidth="1" />
                        <line x1="5" y1="11" x2="8" y2="8" stroke="white" strokeWidth="1" />
                        <line x1="11" y1="11" x2="8" y2="8" stroke="white" strokeWidth="1" />
                      </g>
                    </>
                  ) : (
                    <g transform={`translate(${getTracePosition.x - 14}, ${getTracePosition.y - 14})`}>
                      {/* Forklift icon - SVG from public folder */}
                      <image
                        href="/Forklift.svg"
                        x="0"
                        y="0"
                        width="28"
                        height="28"
                        preserveAspectRatio="xMidYMid meet"
                      />
                    </g>
                  )}
                </g>
              );
            })()}
          </g>
        )}

        {/* Assets */}
        {showAssets && data.assets.filter(a => {
          // Skip assets without a valid position
          if (!a.position) return false;
          // Exclude scrapped assets from the map view
          if (a.status === 'Scrapped') return false;
          // Exclude assets without an active PLX tag (fiducial_marker_id)
          // Only show assets that have a fiducial_marker_id value (not null or undefined)
          // Allow 0 as a valid marker ID
          if (a.fiducial_marker_id == null) return false;
          const zone = zonesById[a.zoneId];
          // Exclude assets from zones that are actually warehouses (have GPS polygons)
          if (zone && zone.gps && zone.gps.polygon) return false;
          return true;
        }).map((a) => {
          const { dx, dy } = getZoneOffset(a.zoneId);
          
          // Use dragging position if this asset is being dragged
          const isDragging = draggingAsset?.id === a.id;
          const assetX = isDragging ? draggingAsset.currentX : a.position.x;
          const assetY = isDragging ? draggingAsset.currentY : a.position.y;
          const cx = assetX + dx;
          const cy = assetY + dy;
          const isSelected = selectedAssetId === a.id;
          const isInSelectedRack = selectedRackAssets.some(asset => asset.id === a.id);
          const isTypeMatch = selectedAssetType ? a.assetType === selectedAssetType : true;
          
          // Selected type: ping all matches and hide others
          const shouldPing = selectedAssetType ? isTypeMatch : isSelected;
          
          // Make assets bigger and more visible in edit mode
          const baseR = editMode ? 6 : ((isSelected || isInSelectedRack || (selectedAssetType && isTypeMatch)) ? 3 : 1);
          // If this asset is of the active type that has anomalies, optionally dim normal dots to make anomaly pings stand out
          const fill = editMode 
            ? (isDragging ? '#60a5fa' : '#3b82f6')
            : selectedAssetType
              ? (isTypeMatch ? '#22c55e' : '#9ca3af')
              : (isSelected ? '#22c55e' : (isInSelectedRack ? '#3b82f6' : '#9ca3af'));
          const dimOpacityByType = selectedAssetType && !isTypeMatch ? 0.2 : 1;
          const finalOpacity = editMode ? 1 : dimOpacityByType;
          
          return (
            <g 
              key={a.id} 
              className={editMode ? "cursor-move" : "cursor-pointer"} 
              opacity={finalOpacity} 
              onClick={(e) => { 
                e.stopPropagation();
                if (!editMode && onAssetSelect) onAssetSelect(a.id);
              }}
              onMouseDown={(e) => { if (editMode) handleAssetDragStart(e, a); }}
            >
              {/* Dragging indicator */}
              {isDragging && (
                <circle cx={cx} cy={cy} r={baseR + 4} fill="none" stroke="#60a5fa" strokeWidth={2} strokeDasharray="4 2" />
              )}
              {/* ping ring when selected */}
              {shouldPing && !editMode && (
                <motion.circle
                  cx={cx}
                  cy={cy}
                  r={baseR}
                  fill="none"
                  stroke="#22c55e"
                  strokeWidth={2}
                  initial={{ r: baseR + 1, opacity: 0.9 }}
                  animate={{ r: baseR + 8, opacity: 0 }}
                  transition={{ repeat: Infinity, duration: 1.2, ease: 'easeOut' }}
                />
              )}
              {/* main dot */}
              <circle cx={cx} cy={cy} r={baseR} fill={fill} />
              {/* Edit mode label */}
              {editMode && (
                <text
                  x={cx}
                  y={cy - baseR - 4}
                  textAnchor="middle"
                  fontSize="8"
                  fill={isDark ? "#94a3b8" : "#64748b"}
                  pointerEvents="none"
                >
                  {a.id}
                </text>
              )}
            </g>
          );
        })}

        {/* Hubs - ESP32 gateway devices - Only shown in warehouse view (not Google Maps) */}
        {!useGoogleView && showHubs && hubs && hubs.filter(h => {
          // Show hubs that match current warehouse, or hubs with no warehouse_id set
          const matchesWarehouse = h.warehouse_id === currentWarehouseId || (!h.warehouse_id && currentWarehouseId);
          // Only show hubs that have a position set
          const hasPosition = h.position && typeof h.position.x === 'number' && typeof h.position.y === 'number';
          if (matchesWarehouse && !hasPosition) {
            console.warn(`Hub ${h.name} (${h.hub_id}) has no position - skipping render`);
          }
          return matchesWarehouse && hasPosition;
        }).map((hub) => {
          const isDragging = draggingHub?.id === hub.id;
          const hubX = isDragging ? draggingHub.currentX : hub.position.x;
          const hubY = isDragging ? draggingHub.currentY : hub.position.y;
          const isSelected = selectedHubId === hub.id;
          const isOnline = hub.status === 'active';
          const isEditing = editingHubId === hub.id;
          
          return (
            <g 
              key={hub.id}
              className={editMode ? "cursor-move" : "cursor-pointer"}
              onMouseEnter={() => setHoveredHubId(hub.id)}
              onMouseLeave={() => setHoveredHubId(null)}
              onMouseDown={(e) => { if (editMode && !isEditing) handleHubDragStart(e, hub); }}
              onClick={(e) => {
                e.stopPropagation();
                if (isEditing) return; // Don't change selection while editing
                if (editMode && isSelected) {
                  // Click on selected hub in edit mode: start renaming
                  setEditingHubId(hub.id);
                  setEditingHubName(hub.name);
                } else {
                  setSelectedHubId(isSelected ? null : hub.id);
                  setEditingHubId(null);
                }
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                if (editMode && onHubNameUpdate) {
                  setEditingHubId(hub.id);
                  setEditingHubName(hub.name);
                }
              }}
            >
              {/* Signal rings for online hubs */}
              {isOnline && (
                <>
                  <motion.circle
                    cx={hubX}
                    cy={hubY}
                    r={3}
                    fill="none"
                    stroke="#22d3ee"
                    strokeWidth={1}
                    initial={{ r: 3, opacity: 0.6 }}
                    animate={{ r: 15, opacity: 0 }}
                    transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
                  />
                  <motion.circle
                    cx={hubX}
                    cy={hubY}
                    r={3}
                    fill="none"
                    stroke="#22d3ee"
                    strokeWidth={1}
                    initial={{ r: 3, opacity: 0.6 }}
                    animate={{ r: 15, opacity: 0 }}
                    transition={{ repeat: Infinity, duration: 2, ease: 'easeOut', delay: 1 }}
                  />
                </>
              )}
              
              {/* Hub body - small dot */}
              <circle
                cx={hubX}
                cy={hubY}
                r={isDragging ? 4 : 3}
                fill={isEditing ? '#7c3aed' : (isOnline ? '#0891b2' : '#6b7280')}
                stroke={isEditing ? '#a78bfa' : (isSelected ? '#22d3ee' : (isOnline ? '#06b6d4' : '#9ca3af'))}
                strokeWidth={isSelected || isEditing ? 2 : 1}
              />
              
              {/* Hub label - show input if editing, otherwise only on hover */}
              {isEditing ? (
                <foreignObject
                  x={hubX - 50}
                  y={hubY + 8}
                  width={100}
                  height={24}
                >
                  <input
                    type="text"
                    value={editingHubName}
                    onChange={(e) => setEditingHubName(e.target.value)}
                    onBlur={async () => {
                      if (editingHubName.trim() && editingHubName.trim() !== hub.name && onHubNameUpdate) {
                        try {
                          await onHubNameUpdate(hub.id, editingHubName.trim());
                        } catch (err) {
                          console.error('Failed to rename hub:', err);
                        }
                      }
                      setEditingHubId(null);
                      setEditingHubName('');
                    }}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter') {
                        if (editingHubName.trim() && editingHubName.trim() !== hub.name && onHubNameUpdate) {
                          try {
                            await onHubNameUpdate(hub.id, editingHubName.trim());
                          } catch (err) {
                            console.error('Failed to rename hub:', err);
                          }
                        }
                        setEditingHubId(null);
                        setEditingHubName('');
                      } else if (e.key === 'Escape') {
                        setEditingHubId(null);
                        setEditingHubName('');
                      }
                    }}
                    autoFocus
                    className={`w-full px-1 py-0 text-xs text-center border border-purple-500 rounded focus:outline-none focus:ring-1 focus:ring-purple-400 ${
                      isDark 
                        ? 'bg-gray-800 text-white' 
                        : 'bg-white text-gray-900'
                    }`}
                    style={{ fontSize: '10px', height: '18px' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  />
                </foreignObject>
              ) : (
                hoveredHubId === hub.id && (
                  <text
                    x={hubX}
                    y={hubY + 12}
                    textAnchor="middle"
                    fontSize="9"
                    fontWeight="500"
                    fill={isDark ? "#e2e8f0" : "#1e293b"}
                  >
                    {hub.name}
                  </text>
                )
              )}
              
              {/* Hub ID label (small, below name) - only on hover */}
              {!isEditing && hub.hub_id && hoveredHubId === hub.id && (
                <text
                  x={hubX}
                  y={hubY + 21}
                  textAnchor="middle"
                  fontSize="7"
                  fill={isDark ? "#9ca3af" : "#64748b"}
                  className="pointer-events-none"
                >
                  {hub.hub_id}
                </text>
              )}
              
              {/* Edit hint when selected in edit mode */}
              {editMode && isSelected && !isEditing && (
                <text
                  x={hubX}
                  y={hubY + 30}
                  textAnchor="middle"
                  fontSize="7"
                  fill="#a78bfa"
                  className="pointer-events-none"
                >
                  double-click to rename
                </text>
              )}
            </g>
          );
        })}
        
        {/* Hub placement cursor */}
        {hubPlacementMode && (
          <text
            x="50%"
            y="30"
            textAnchor="middle"
            fontSize="12"
            fill="#22d3ee"
            stroke={isDark ? "#0f172a" : "#ffffff"}
            strokeWidth={2}
            paintOrder="stroke"
            className="pointer-events-none"
          >
            Click to place an ESP32 hub
          </text>
        )}

        {/* Tracked BLE Beacons - Triangulated positions from hub readings */}
        {!useGoogleView && filteredBeacons.map((beacon) => {
          const { bleTagId, position, confidence, hubCount, hubs: beaconHubs, avgRssi } = beacon;
          
          if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
            return null;
          }
          
          // Color based on confidence: green (high) -> yellow (medium) -> red (low)
          const getBeaconColor = (conf) => {
            if (conf >= 0.7) return '#22c55e'; // green
            if (conf >= 0.4) return '#eab308'; // yellow
            return '#f97316'; // orange
          };
          
          const beaconColor = getBeaconColor(confidence);
          const isHighConfidence = confidence >= 0.5;
          
          // Format BLE tag ID for display (show last 8 chars if long)
          const displayId = bleTagId.length > 12 
            ? '...' + bleTagId.slice(-8) 
            : bleTagId;
          
          return (
            <g key={`beacon-${bleTagId}`} className="pointer-events-auto cursor-pointer">
              {/* Confidence ring - larger and more transparent for lower confidence */}
              <motion.circle
                cx={position.x}
                cy={position.y}
                r={isHighConfidence ? 25 : 35}
                fill={beaconColor}
                fillOpacity={0.1}
                stroke={beaconColor}
                strokeWidth={1}
                strokeOpacity={0.3}
                strokeDasharray={isHighConfidence ? "none" : "4,4"}
              />
              
              {/* Pulsing outer ring */}
              <motion.circle
                cx={position.x}
                cy={position.y}
                r={8}
                fill="none"
                stroke={beaconColor}
                strokeWidth={2}
                initial={{ r: 8, opacity: 0.8 }}
                animate={{ r: 20, opacity: 0 }}
                transition={{ repeat: Infinity, duration: 1.5, ease: 'easeOut' }}
              />
              
              {/* Beacon marker */}
              <circle
                cx={position.x}
                cy={position.y}
                r={8}
                fill={beaconColor}
                stroke="#ffffff"
                strokeWidth={2}
              />
              
              {/* BLE icon inside */}
              <g transform={`translate(${position.x - 4}, ${position.y - 4})`}>
                <path
                  d="M4 0L4 8M2 2L4 4L2 6M6 2L4 4L6 6"
                  fill="none"
                  stroke="white"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </g>
              
              {/* Beacon ID label */}
              <text
                x={position.x}
                y={position.y + 18}
                textAnchor="middle"
                fontSize="8"
                fontWeight="600"
                fill="#ffffff"
                className="pointer-events-none"
              >
                {displayId}
              </text>
              
              {/* Hub count and signal strength */}
              <text
                x={position.x}
                y={position.y + 27}
                textAnchor="middle"
                fontSize="7"
                fill={isDark ? "#9ca3af" : "#64748b"}
                className="pointer-events-none"
              >
                {hubCount} hub{hubCount !== 1 ? 's' : ''} • {avgRssi} dBm
              </text>
              
              {/* Confidence indicator */}
              <text
                x={position.x}
                y={position.y + 36}
                textAnchor="middle"
                fontSize="6"
                fill={beaconColor}
                className="pointer-events-none"
              >
                {Math.round(confidence * 100)}% conf
              </text>
              
              {/* Lines to hubs that see this beacon - only show if enabled */}
              {showUWBDistances && beaconHubs && beaconHubs.map((hubInfo, idx) => {
                // Find the hub position in our hubs array
                const hub = hubs.find(h => h.hub_id === hubInfo.id);
                if (!hub || !hub.position) return null;
                
                return (
                  <line
                    key={`beacon-hub-line-${bleTagId}-${hubInfo.id}-${idx}`}
                    x1={position.x}
                    y1={position.y}
                    x2={hub.position.x}
                    y2={hub.position.y}
                    stroke={beaconColor}
                    strokeWidth={1}
                    strokeOpacity={0.3}
                    strokeDasharray="3,3"
                    className="pointer-events-none"
                  />
                );
              })}
            </g>
          );
        })}
        
        {/* Beacon count indicator */}
        {!useGoogleView && filteredBeacons.length > 0 && (
          <g transform="translate(10, 10)">
            <rect
              x={0}
              y={0}
              width={120}
              height={28}
              rx={4}
              fill="rgba(0,0,0,0.7)"
              stroke="#22c55e"
              strokeWidth={1}
            />
            <circle cx={12} cy={14} r={5} fill="#22c55e" />
            <text
              x={22}
              y={18}
              fontSize="10"
              fill={isDark ? "#e2e8f0" : "#1e293b"}
              className="pointer-events-none"
            >
              {filteredBeacons.length} beacon{filteredBeacons.length !== 1 ? 's' : ''} tracked
            </text>
          </g>
        )}

        {/* UWB Tags (Forklifts/Drones) - Real-time tracking */}
        {!useGoogleView && uwbTags && uwbTags.map((tag) => {
          const { tag_id, tag_name, position, confidence, hub_count, tag_hardware_id, warehouse_id, status, positioning_method } = tag;
          
          // Show tags without positions as a question mark indicator in the center of the viewBox
          if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') {
            // Debug: Log why tag isn't rendering
            if (status === 'active') {
              console.warn(`[UWB Tag] Active tag "${tag_name || tag_id}" (${tag_hardware_id}) not rendering - missing position data. Warehouse: ${warehouse_id || 'none'}, Current: ${currentWarehouseId}`);
              console.debug(`[UWB Tag] Tag data:`, { 
                tag_id, 
                tag_name, 
                tag_hardware_id, 
                position, 
                warehouse_id, 
                status,
                hub_count,
                confidence 
              });
            }
            
            // Don't render tags without positions - they need UWB readings first
            // The console warnings will guide the user on what to check
            return null;
          }

          // NO WAREHOUSE FILTERING - show all tags regardless of warehouse_id
          // This is for debugging to see if filtering was the issue

          // Validate position coordinates are reasonable (not in extreme corners)
          // Check if position is within a reasonable range relative to the viewBox
          // This helps catch coordinate system mismatches
          const [vx, vy, vw, vh] = defaultViewBox.split(' ').map(Number);
          const isPositionValid = 
            position.x >= vx - vw * 0.5 && position.x <= vx + vw * 1.5 &&
            position.y >= vy - vh * 0.5 && position.y <= vy + vh * 1.5;
          
          if (!isPositionValid) {
            // Position is way outside the viewBox - likely a coordinate system mismatch
            // This could happen if:
            // 1. Blueprint coordinate system origin is different
            // 2. UWB calibration is incorrect
            // 3. Tags are being calculated in a different coordinate space
            console.warn(`[UWB Tag] Tag ${tag_name || tag_id} (warehouse: ${warehouse_id || 'none'}) has position (${position.x.toFixed(1)}, ${position.y.toFixed(1)}) outside viewBox (${vx.toFixed(1)}, ${vy.toFixed(1)}, ${vw.toFixed(1)}, ${vh.toFixed(1)}) - possible coordinate mismatch. Current warehouse: ${currentWarehouseId}`);
            // Still render it but log a warning - the user can see where it actually is
          }
          
          // Determine if this is a drone or forklift based on tag_hardware_id
          const isDrone = tag_hardware_id === 'TAG001' || tag_name?.toLowerCase().includes('drone');
          const isForklift = tag_hardware_id === 'TAG002' || tag_name?.toLowerCase().includes('forklift');
          
          // Check if forklift is carrying an asset
          const carryingStatus = forkliftCarryingStatus.get(tag_id);
          const isCarrying = carryingStatus?.carrying || false;
          
          // Color based on carrying status: green if carrying, otherwise confidence-based
          const getTagColor = (conf, carrying) => {
            if (carrying) return '#22c55e'; // green when carrying
            if (conf >= 0.7) return '#3b82f6'; // blue
            if (conf >= 0.4) return '#06b6d4'; // cyan
            return '#f97316'; // orange
          };
          
          const tagColor = getTagColor(confidence, isCarrying);
          
          return (
            <g 
              key={`uwb-tag-${tag_id}`} 
              className="pointer-events-auto cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (isCarrying && onForkliftInfoChange) {
                  onForkliftInfoChange({
                    tag: tag,
                    asset: carryingStatus.asset,
                    position: position,
                    confidence: confidence
                  });
                }
              }}
            >
              {/* Pulsing outer ring - no large circle */}
              <motion.circle
                cx={position.x}
                cy={position.y}
                r={10}
                fill="none"
                stroke={tagColor}
                strokeWidth={2.5}
                initial={{ r: 10, opacity: 0.8 }}
                animate={{ r: 25, opacity: 0 }}
                transition={{ repeat: Infinity, duration: 2, ease: 'easeOut' }}
              />
              
              {/* Icon - Drone or Forklift */}
              {isDrone ? (
                <>
                  {/* Icon background circle - only for drone */}
                  <circle
                    cx={position.x}
                    cy={position.y}
                    r={14}
                    fill={tagColor}
                    stroke="#ffffff"
                    strokeWidth={3}
                  />
                  <g transform={`translate(${position.x - 8}, ${position.y - 8})`}>
                    {/* Drone icon */}
                    <circle cx="8" cy="8" r="3" fill="white" />
                    <rect x="6" y="7" width="4" height="2" fill="white" />
                    <circle cx="3" cy="3" r="2" fill="white" opacity="0.8" />
                    <circle cx="13" cy="3" r="2" fill="white" opacity="0.8" />
                    <circle cx="3" cy="13" r="2" fill="white" opacity="0.8" />
                    <circle cx="13" cy="13" r="2" fill="white" opacity="0.8" />
                    <line x1="5" y1="5" x2="8" y2="8" stroke="white" strokeWidth="1" />
                    <line x1="11" y1="5" x2="8" y2="8" stroke="white" strokeWidth="1" />
                    <line x1="5" y1="11" x2="8" y2="8" stroke="white" strokeWidth="1" />
                    <line x1="11" y1="11" x2="8" y2="8" stroke="white" strokeWidth="1" />
                  </g>
                </>
              ) : (
                <g transform={`translate(${position.x - 14}, ${position.y - 14})`}>
                  {/* Forklift icon - SVG from public folder */}
                  <image
                    href="/Forklift.svg"
                    x="0"
                    y="0"
                    width="28"
                    height="28"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </g>
              )}
              
              {/* Method label - small text above tag */}
              {positioning_method && (
                <text
                  x={position.x}
                  y={position.y - 20}
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="600"
                  fill={tagColor}
                  stroke="rgba(0,0,0,0.6)"
                  strokeWidth={2}
                  paintOrder="stroke"
                >
                  {positioning_method === 'pairwise' ? 'PW' :
                   positioning_method === 'pairwise2' ? 'PW2' :
                   positioning_method === 'linear' ? 'LIN' :
                   positioning_method === 'linear2' ? 'LN2' :
                   positioning_method}
                </text>
              )}
              
              {/* Tooltip on hover */}
              <title>{tag_name || (isDrone ? 'Drone' : 'Forklift')} {positioning_method ? `(${positioning_method})` : ''}</title>
              
              {/* Lines to hubs with distance readings (like Python GUI) - only show if actively receiving data */}
              {showUWBDistances && hubs && hubs.filter(h => h.uwb_anchor_id && h.position).map((hub, idx) => {
                // Check if this hub-tag pair has active readings (within last 15 seconds)
                // Find matching reading by tag hardware ID or database ID
                // tag_id in the tag object is the database ID, tag_hardware_id is the hardware ID
                const tag = uwbTags?.find(t => t.tag_id === tag_id || t.tag_hardware_id === tag_id);
                if (!tag) return null;
                
                // Match by either database ID or hardware ID
                const reading = Array.from(activeHubTagReadings.values()).find(r => 
                  (r.tagId === tag.tag_id || r.tagHardwareId === tag.tag_hardware_id || r.tagHardwareId === tag_id) && 
                  r.hubId === hub.id
                );
                
                // Only show line if hub is actively receiving data from this tag
                if (!reading) return null;
                
                // Use the actual distance from the reading (in meters)
                const distanceInMeters = reading.latestDistance;
                
                // Get blueprint calibration to determine unit
                const currentWarehouse = warehouses[currentWarehouseId];
                const blueprintScale = currentWarehouse?.blueprint?.scale;
                
                let realWorldDistance = distanceInMeters;
                let distanceUnit = 'm';
                
                // If calibration was done in feet, convert to feet
                if (blueprintScale && blueprintScale.calibrationUnit === 'feet') {
                  realWorldDistance = distanceInMeters * 3.28084; // meters to feet
                  distanceUnit = 'ft';
                }
                
                // Midpoint for distance label
                const midX = (position.x + hub.position.x) / 2;
                const midY = (position.y + hub.position.y) / 2;
                
                return (
                  <g key={`uwb-hub-line-${tag_id}-${hub.id}-${idx}`}>
                    {/* Line to hub */}
                    <line
                      x1={position.x}
                      y1={position.y}
                      x2={hub.position.x}
                      y2={hub.position.y}
                      stroke={tagColor}
                      strokeWidth={2}
                      strokeOpacity={0.4}
                      strokeDasharray="4,4"
                      className="pointer-events-none"
                    />
                    
                    {/* Distance label background */}
                    <rect
                      x={midX - 25}
                      y={midY - 8}
                      width={50}
                      height={16}
                      rx={3}
                      fill="rgba(0,0,0,0.8)"
                      stroke={tagColor}
                      strokeWidth={1}
                    />
                    
                    {/* Distance text */}
                    <text
                      x={midX}
                      y={midY + 4}
                      textAnchor="middle"
                      fontSize="8"
                      fontWeight="600"
                      fill={tagColor}
                      className="pointer-events-none"
                    >
                      {realWorldDistance.toFixed(1)}{distanceUnit}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* ── ALL-mode ghost tags ─────────────────────────────────────────── */}
        {!useGoogleView && positioningMethod === 'all' && ghostTags && ghostTags
          .filter(ghost => ['pairwise', 'linear', 'pairwise2', 'linear2'].includes(ghost.positioning_method))
          .map((ghost) => {
          const { tag_id, position, positioning_method, tag_name, confidence, tag_hardware_id, real_tag_id } = ghost;
          if (!position || typeof position.x !== 'number' || typeof position.y !== 'number') return null;

          // Color per method
          const methodMeta = {
            pairwise: { color: '#f97316', label: 'PW' },
            pairwise2: { color: '#ea580c', label: 'PW2' },
            linear:   { color: '#10b981', label: 'LIN' },
            linear2:   { color: '#059669', label: 'LN2' },
          };
          const meta = methodMeta[positioning_method] || { color: '#9ca3af', label: '?' };

          return (
            <g key={`ghost-${tag_id}-${positioning_method}`} className="pointer-events-none">
              {/* Pulsing ring */}
              <motion.circle
                cx={position.x}
                cy={position.y}
                r={8}
                fill="none"
                stroke={meta.color}
                strokeWidth={1.5}
                initial={{ r: 8, opacity: 0.7 }}
                animate={{ r: 20, opacity: 0 }}
                transition={{ repeat: Infinity, duration: 2.5, ease: 'easeOut' }}
              />
              {/* Filled dot */}
              <circle
                cx={position.x}
                cy={position.y}
                r={7}
                fill={meta.color}
                stroke="#ffffff"
                strokeWidth={2}
                opacity={0.92}
              />
              {/* Method label inside dot */}
              <text
                x={position.x}
                y={position.y + 3}
                textAnchor="middle"
                fontSize="5"
                fontWeight="700"
                fill="white"
              >
                {meta.label}
              </text>
              {/* Tag name above */}
              <text
                x={position.x}
                y={position.y - 11}
                textAnchor="middle"
                fontSize="7"
                fontWeight="600"
                fill={meta.color}
                stroke="rgba(0,0,0,0.6)"
                strokeWidth={2}
                paintOrder="stroke"
              >
                {tag_name ? `${tag_name} · ${meta.label}` : meta.label}
              </text>
              <title>{`${tag_name || 'Tag'} — ${meta.label} method (conf: ${((confidence || 0) * 100).toFixed(0)}%)`}</title>
              
              {/* Lines to hubs with distance readings (like main UWB tags) */}
              {showUWBDistances && hubs && hubs.filter(h => h.uwb_anchor_id && h.position).map((hub, idx) => {
                // Find matching reading by tag hardware ID or database ID
                // Use real_tag_id if available, otherwise use tag_id or tag_hardware_id
                const tagIdToMatch = real_tag_id || tag_id;
                const tagHardwareIdToMatch = tag_hardware_id;
                
                // Match by either database ID or hardware ID
                const reading = Array.from(activeHubTagReadings.values()).find(r => 
                  (r.tagId === tagIdToMatch || r.tagHardwareId === tagHardwareIdToMatch) && 
                  r.hubId === hub.id
                );
                
                // Only show line if hub is actively receiving data from this tag
                if (!reading) return null;
                
                // Use the actual distance from the reading (in meters)
                const distanceInMeters = reading.latestDistance;
                
                // Get blueprint calibration to determine unit
                const currentWarehouse = warehouses[currentWarehouseId];
                const blueprintScale = currentWarehouse?.blueprint?.scale;
                
                let realWorldDistance = distanceInMeters;
                let distanceUnit = 'm';
                
                // If calibration was done in feet, convert to feet
                if (blueprintScale && blueprintScale.calibrationUnit === 'feet') {
                  realWorldDistance = distanceInMeters * 3.28084; // meters to feet
                  distanceUnit = 'ft';
                }
                
                // Midpoint for distance label
                const midX = (position.x + hub.position.x) / 2;
                const midY = (position.y + hub.position.y) / 2;
                
                return (
                  <g key={`ghost-hub-line-${tag_id}-${positioning_method}-${hub.id}-${idx}`}>
                    {/* Line to hub */}
                    <line
                      x1={position.x}
                      y1={position.y}
                      x2={hub.position.x}
                      y2={hub.position.y}
                      stroke={meta.color}
                      strokeWidth={2}
                      strokeOpacity={0.4}
                      strokeDasharray="4,4"
                      className="pointer-events-none"
                    />
                    
                    {/* Distance label background */}
                    <rect
                      x={midX - 25}
                      y={midY - 8}
                      width={50}
                      height={16}
                      rx={3}
                      fill="rgba(0,0,0,0.8)"
                      stroke={meta.color}
                      strokeWidth={1}
                    />
                    
                    {/* Distance text */}
                    <text
                      x={midX}
                      y={midY + 4}
                      textAnchor="middle"
                      fontSize="8"
                      fontWeight="600"
                      fill={meta.color}
                      className="pointer-events-none"
                    >
                      {realWorldDistance.toFixed(1)}{distanceUnit}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* ALL-mode legend */}
        {!useGoogleView && positioningMethod === 'all' && (
          <g>
            {[
              { key: 'pairwise', color: '#f97316', label: 'Pairwise' },
              { key: 'linear',   color: '#10b981', label: 'Linear' },
            ].map(({ key, color, label }, i) => {
              const [vx, vy] = defaultViewBox.split(' ').map(Number);
              const lx = vx + 12;
              const ly = vy + 14 + i * 16;
              return (
                <g key={`legend-${key}`}>
                  <rect x={lx - 2} y={ly - 8} width={70} height={12} rx={3} fill="rgba(0,0,0,0.55)" />
                  <circle cx={lx + 5} cy={ly - 2} r={4} fill={color} />
                  <text x={lx + 13} y={ly + 2} fontSize="8" fontWeight="600" fill={color}>{label}</text>
                </g>
              );
            })}
          </g>
        )}

        </motion.svg>
      </motion.div>

      {/* Conflict Dialog */}
      {conflictDialog && (
        <WarehouseConflictDialog
          warehouseName={conflictDialog.name}
          existingWarehouse={conflictDialog.existingWarehouse}
          onOverride={handleOverride}
          onUpdate={handleUpdate}
          onCancel={handleCancelConflict}
        />
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 py-1 min-w-[150px]"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.type === 'vertex' && (
            <button
              onClick={() => handleDeleteCorner(contextMenu.index)}
              className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-700"
            >
              Delete Corner
            </button>
          )}
        </div>
      )}

      {/* Blueprint Editor Dialog */}
      {showBlueprintEditor && (
        <FeatureGate 
          user={user} 
          featureKey="blueprint_editor"
          blur={true}
          showUpgradePrompt={true}
          upgradePlanName="Pro"
          customMessage="Blueprint editing requires the Pro plan or higher."
        >
          <BlueprintEditor
            blueprint={data.blueprint}
            onSave={(updatedBlueprint) => {
              if (onBlueprintUpdate) {
                saveBlueprintToHistory(data.blueprint);
                onBlueprintUpdate(updatedBlueprint);
              }
              setShowBlueprintEditor(false);
            }}
            onCancel={() => setShowBlueprintEditor(false)}
          />
        </FeatureGate>
      )}

      {/* Google Map view */}
      <motion.div
        className="absolute inset-0 z-0"
        initial={{ opacity: 0, scale: 1.02 }}
        animate={{ opacity: useGoogleView ? 1 : 0, scale: useGoogleView ? 1 : 1.02 }}
        transition={{ duration: 0.45, ease: 'easeInOut' }}
        style={{ pointerEvents: useGoogleView ? 'auto' : 'none' }}
      >
        <GoogleMapView
          center={googleCenter}
          zoom={googleZoom}
          assets={externalAssets}
          zones={data.zones}
          warehouses={Object.values(warehouses).filter(wh => wh.gps && wh.gps.polygon)}
          selectedAssetId={selectedAssetId}
          selectedZoneId={selectedZoneId}
          onAssetClick={(id) => { if (onAssetSelect) onAssetSelect(id); }}
          onWarehouseClick={onWarehouseClick}
          warehouseNavTarget={warehouseNavTarget}
          buildMode={buildMode}
          buildPoints={buildPoints}
          onMapClick={handleAddBuildPoint}
          onBuildPointDelete={handleDeleteBuildPoint}
          selectedPointIndex={selectedPointIndex}
          onPointSelect={setSelectedPointIndex}
          mapTypeId={mapType}
          blueprintEditMode={blueprintEditMode && useGoogleView}
          onBlueprintUpdate={onBlueprintUpdate}
          currentBlueprint={data.blueprint}
          currentWarehouse={warehouses[currentWarehouseId]}
          gpsEditMode={gpsBuildSubMode === 'edit' && editingWarehouseId !== null}
          onBuildPointUpdate={handleBuildPointUpdate}
          editingWarehouseId={editingWarehouseId}
        />
      </motion.div>
      
      {/* Hub Creation Modal */}
      {showHubModal && (
        <HubCreationModal
          onConfirm={handleCreateHubFromModal}
          onCancel={() => {
            setShowHubModal(false);
            setPendingHubPosition(null);
          }}
          warehouseName={warehouses[currentWarehouseId]?.name || warehouses[currentWarehouseId]?.display_name || 'Warehouse'}
          hubNumber={(hubs.filter(h => h.warehouse_id === currentWarehouseId).length + 1)}
          isDark={isDark}
        />
      )}
      
      {/* UWB Tag Manager */}
      {showTagManager && (
        <FeatureGate 
          user={user} 
          featureKey="uwb_tracking"
          blur={true}
          showUpgradePrompt={true}
          upgradePlanName="Plus"
          customMessage="UWB tracking requires the Plus plan or higher."
        >
          <UWBTagManager
            warehouseId={currentWarehouseId}
            onClose={() => setShowTagManager(false)}
          />
        </FeatureGate>
      )}

      {/* UWB Auto-Calibration */}
      {showUWBCalibration && (
        <FeatureGate 
          user={user} 
          featureKey="uwb_tracking"
          blur={true}
          showUpgradePrompt={true}
          upgradePlanName="Plus"
          customMessage="UWB auto-calibration requires the Plus plan or higher."
        >
          <UWBCalibration
            warehouseId={currentWarehouseId}
            onClose={() => setShowUWBCalibration(false)}
            onComplete={(result) => {
              console.log('Calibration complete:', result);
              setShowUWBCalibration(false);
              // Refresh the page to show new anchor positions
              window.location.reload();
            }}
          />
        </FeatureGate>
      )}
    </div>
  );
}

// Hub Creation Modal Component
function HubCreationModal({ onConfirm, onCancel, warehouseName, hubNumber, isDark }) {
  const [uwbAnchorId, setUwbAnchorId] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Validate UWB Anchor ID (should be 4 hex characters)
    if (!uwbAnchorId || !/^[0-9A-Fa-f]{4}$/.test(uwbAnchorId)) {
      setError('UWB Anchor ID must be 4 hexadecimal characters (e.g., A43F)');
      return;
    }

    // Validate MAC Address (optional but if provided, should be valid format)
    if (macAddress && !/^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/.test(macAddress)) {
      setError('MAC Address must be in format XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX');
      return;
    }

    onConfirm(uwbAnchorId.toUpperCase(), macAddress.toUpperCase());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${isDark ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'} rounded-lg shadow-xl p-6 w-full max-w-md`}>
        <h2 className="text-xl font-bold mb-4">Add New Hub</h2>
        
        <div className="mb-4 p-3 bg-blue-500 bg-opacity-10 border border-blue-500 rounded">
          <p className="text-sm">
            <strong>Auto-generated name:</strong> Hub {hubNumber} ({warehouseName})
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              UWB Anchor ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={uwbAnchorId}
              onChange={(e) => setUwbAnchorId(e.target.value)}
              placeholder="A43F"
              maxLength={4}
              className={`w-full px-3 py-2 border rounded-md font-mono uppercase ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
              required
            />
            <p className="text-xs mt-1 opacity-70">
              Last 2 bytes of MAC address (4 hex characters)
            </p>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">
              Full MAC Address <span className="text-gray-500">(optional)</span>
            </label>
            <input
              type="text"
              value={macAddress}
              onChange={(e) => setMacAddress(e.target.value)}
              placeholder="78:1C:3C:AE:3F:A4"
              className={`w-full px-3 py-2 border rounded-md font-mono uppercase ${
                isDark 
                  ? 'bg-gray-700 border-gray-600 text-white' 
                  : 'bg-white border-gray-300 text-gray-900'
              }`}
            />
            <p className="text-xs mt-1 opacity-70">
              Format: XX:XX:XX:XX:XX:XX (for reference)
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-500 bg-opacity-10 border border-red-500 rounded text-sm">
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 px-4 py-2 rounded-md border ${
                isDark
                  ? 'border-gray-600 hover:bg-gray-700'
                  : 'border-gray-300 hover:bg-gray-100'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700"
            >
              Create Hub
            </button>
          </div>
        </form>

        <div className="mt-4 pt-4 border-t border-gray-600">
          <p className="text-xs opacity-70">
            <strong>Examples:</strong><br/>
            • A43F (from 78:1C:3C:AE:3F:A4)<br/>
            • B4DD (from 6C:C8:40:7D:DD:B4)<br/>
            • 3C39 (from 78:1C:3C:B0:39:3C)
          </p>
        </div>
      </div>
    </div>
  );
}



